// van Gelder Inc. — Temporary catalog data (Old-Mock-Beta)
// EDIT THIS FILE to update categories, product names, descriptions, and specs.

const slugify = (str) =>
  str
    .toLowerCase()
    .replace(/®|™/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Category images (replace with real assets later)
const IMG = {
  sheetRoll: "/products/categories/sheet-roll.jpg",
  carpetTile: "/products/categories/carpet-tile-lobby.jpg",
  customLogos: "/products/categories/custom-logos-welcome-v2.jpg",
  luxuryVinyl: "/products/categories/luxury-vinyl-retail-v2.jpg",
  gridSystems: "/products/categories/grid-systems-strax.jpg",
  commercialMats: "/products/categories/commercial-mats-lobby-v3.jpg",
  industrial: "/products/categories/industrial.jpg",
  recycledRubber: "/products/categories/recycled-rubber-spin-v2.jpg",
  accessories:
    "https://images.unsplash.com/photo-1760378367757-af1a1a7f062e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

// Raw category definitions with product name lists (order matters).
const RAW = [
  {
    slug: "broadloom-sheet-roll-goods",
    name: "Broadloom Sheet/Roll Goods",
    image: IMG.sheetRoll,
    blurb:
      "Roll-format broadloom and floor matting engineered for high-traffic commercial environments.",
    products: [
      "Atlas",
      "Champion Super Nop",
      "Cougar",
      "Eagle",
      "Fenway Nop",
      "Natural Coco/Coir",
      "Synthetic Coco",
      "Windsor",
    ],
  },
  {
    slug: "carpet-tile-goods",
    name: "Carpet Tile Goods",
    image: IMG.carpetTile,
    blurb:
      "Modular carpet tiles offering design flexibility, easy replacement, and durable performance.",
    products: [
      "Champion Super Nop Tile",
      "Champion XP",
      "Champion LP",
      "Dominator",
      "Dominator LP",
      "Diplomat",
      "Atlas Plus",
      "Windsor",
      "Senator SoftStep™ Plus",
      "Griffin Tile",
    ],
  },
  {
    slug: "custom-logos",
    name: "Custom Logos",
    image: IMG.customLogos,
    blurb:
      "Custom logo and branded matting to make a statement at any entrance or lobby.",
    products: [
      "Champion Super Nop (CSN Customs)",
      "Two-N-One Mat",
      "Spectra Print H.D.",
      "Natural Coco Custom",
      "Synthetic Coco Custom",
      "Windsor",
      "Pinnacle H.D.",
    ],
  },
  {
    slug: "luxury-vinyl-plank-tiles",
    name: "Luxury Vinyl Plank & Tiles",
    image: IMG.luxuryVinyl,
    blurb:
      "Luxury vinyl planks and tile that combine realistic visuals with commercial durability.",
    products: [
      'Natures View™ Style Rustique 7" x 48"',
      'Natures View™ Style Rustique 18" x 18"',
      'Natures View™ Style Rustique 18" x 36"',
    ],
  },
  {
    slug: "entry-grid-systems",
    name: "Entry Grid Systems",
    image: IMG.gridSystems,
    blurb:
      "Modular grid matting systems that trap dirt and moisture at building entrances and wet areas.",
    products: [
      "Snap Trax Tile",
      "Snap Trax Plus",
      "Frontrunner",
      "Frontrunner Plus",
      "Frontrunner SB",
      "Heron Tile",
      "Heron Rib",
    ],
  },
  {
    slug: "commercial-mats",
    name: "Commercial Mats",
    image: IMG.commercialMats,
    imgPosition: "bottom",
    blurb:
      "General-purpose commercial matting for entrances, walk-off zones, and high-traffic interior areas.",
    products: [
      "Tidal + Diamond Mat",
      "Channel Rib",
      "SuperTac Matting",
    ],
  },
  {
    slug: "industrial-anti-fatigue-mats",
    name: "Industrial & Anti-Fatigue Mats",
    image: IMG.industrial,
    blurb:
      "Anti-fatigue and industrial matting built to protect workers and equipment in demanding settings.",
    products: [
      "Comfort Step 42",
      "Comfort Step Diamond",
      "Kitchen Mat",
      "Diamond Plate Safety Step",
      "Bubble Mat",
      "Elite Step",
      "Luxury Step",
      "Marble Step",
    ],
  },
  {
    slug: "elements-recycled-rubber-flooring",
    name: "Elements Recycled Rubber Flooring",
    image: IMG.recycledRubber,
    imgPosition: "bottom",
    blurb:
      "Durable recycled rubber flooring for fitness, industrial, and sound-sensitive spaces.",
    products: [
      "Elements 100 Series",
      "Elements 200 Series",
      "Elements 350 Series",
      "Elements 950 Series",
      "Monster Tile – 100-350-950 Series",
    ],
  },
];

const applicationsFor = (catName) => [
  "High-traffic commercial entrances",
  "Corporate offices and lobbies",
  "Retail and hospitality spaces",
  "Healthcare and education facilities",
];

const featuresFor = (catName) => [
  `Category: ${catName}`,
  "Commercial-grade durability",
  "Available in multiple sizes and colors",
  "Backed by van Gelder quality standards",
  "Specifications available on request",
];

// Per-product real content. Key: "<categorySlug>/<productSlug>".
// description: array of paragraphs. features: array of { label, value }.
const OVERRIDES = {
  "commercial-mats/channel-rib": {
    productPdf: "/docs/commercial-mats/channel-rib_product-sheet.pdf",
    techSpecs: "/docs/commercial-mats/channel-rib_technical-specs.pdf",
    image: "/products/channel-rib/hero.jpg",
    gallery: [
      { src: "/products/channel-rib/hero.jpg", label: "Commercial entrance" },
      { src: "/products/channel-rib/store-entrance.jpg", label: "Retail storefront" },
      { src: "/products/channel-rib/brick-corner.jpg", label: "Office vestibule" },
    ],
    swatches: [
      { name: "Charcoal", src: "/products/channel-rib/swatches/charcoal.jpg" },
      { name: "Dark Grey", src: "/products/channel-rib/swatches/dark-grey.jpg" },
      { name: "Slate Blue", src: "/products/channel-rib/swatches/slate-blue.jpg" },
      { name: "Evergreen", src: "/products/channel-rib/swatches/evergreen.jpg" },
      { name: "Brown", src: "/products/channel-rib/swatches/brown.jpg" },
      { name: "Red", src: "/products/channel-rib/swatches/red.jpg" },
    ],
    swatchCount: 6,
    description: [
      "Channel Rib brings purposeful texture and understated character to hardworking commercial entrances. Its classic ribbed surface creates a clean, linear pattern that helps guide moisture away while allowing captured dirt and debris to be easily swept or vacuumed.",
      "Crafted from 100% solution-dyed, UV-stabilized polypropylene and supported by a durable vinyl backing, Channel Rib performs reliably across indoor and outdoor applications. Well suited for entryways and ramps, it offers a thoughtful balance of practical design, dependable floor protection, and lasting value.",
    ],
    applications: [
      "Ribbed pattern channels water away while making it easy to sweep off dirt and debris",
      "High-quality vinyl backing",
    ],
    features: [
      { label: "Application", value: "Indoor/Outdoor Matting" },
      { label: "Fiber Content", value: "100% Solution Dyed, UV-stabilized Polypropylene" },
    ],
  },

  "carpet-tile-goods/griffin-tile": {
    productPdf: "/docs/carpet-tile/griffin-tile_product-sheet.pdf",
    techSpecs: "/docs/carpet-tile/griffin-tile_technical-specs.pdf",
    image: "/products/griffin-tile/1_office.jpg",
    gallery: [
      { src: "/products/griffin-tile/1_office.jpg", label: "Corporate reception area" },
      { src: "/products/griffin-tile/2_grocery.jpg", label: "Retail store entrance" },
    ],
    swatches: [
      { name: "Charcoal", src: "/products/griffin-tile/swatches/charcoal.jpg" },
      { name: "Grey", src: "/products/griffin-tile/swatches/grey.jpg" },
      { name: "Brown", src: "/products/griffin-tile/swatches/brown.jpg" },
    ],
    swatchCount: 3,
    description: [
      "Griffin Tile brings clean, modern character to active office environments. Its designer loop-pile surface and low-profile construction create a polished foundation that supports effortless movement of furniture, office chairs, and rolling carts.",
      "Crafted for constant foot traffic, Griffin Tile balances refined styling with dependable everyday durability. It is a practical, lasting flooring solution for offices, corridors, reception areas, and other professional spaces that rarely slow down.",
    ],
    applications: [
      "Construction: Nonwoven Twisted Loop Pile Structure",
      "Fiber: 28% Globe Nylon + 72% Polypropylene Scraper Yarn",
      "Backing: Eco Bitumen",
    ],
    features: [
      "24 oz. Nonwoven Twisted Loop Scraper",
      "Pile Weight: 24 oz/yd\u00b2",
      "Tile Size: 19.69\u201d x 19.69\u201d",
    ],
  },
  "carpet-tile-goods/dominator-lp": {
    productPdf: "/docs/carpet-tile/dominator-lp_product-sheet.pdf",
    techSpecs: "/docs/carpet-tile/dominator-lp_technical-specs.pdf",
    image: "/products/dominator-lp/1_office.jpg",
    swatches: [
      { name: "Anthracite", src: "/products/dominator-lp/swatches/anthracite.jpg" },
      { name: "Autumn Green", src: "/products/dominator-lp/swatches/autumn-green.jpg" },
      { name: "Black Shadow", src: "/products/dominator-lp/swatches/black-shadow.jpg" },
      { name: "Cranberry", src: "/products/dominator-lp/swatches/cranberry.jpg" },
      { name: "Mid Grey", src: "/products/dominator-lp/swatches/mid-grey.jpg" },
      { name: "Olive", src: "/products/dominator-lp/swatches/olive.jpg" },
      { name: "Pebble", src: "/products/dominator-lp/swatches/pebble.jpg" },
    ],
    gallery: [
      { src: "/products/dominator-lp/1_office.jpg", label: "Office entrance" },
      { src: "/products/dominator-lp/2_store-entrance.jpg", label: "Retail store entrance" },
      { src: "/products/dominator-lp/3_retail.jpg", label: "Wireless retail showroom" },
    ],
    description: [
      "Dominator LP brings the proven strength of Dominator to a lighter, more adaptable carpet tile. Its darker foundation subtly reveals ribbed color accents, creating visual depth and a tailored appearance for demanding commercial interiors.",
      "Designed for heavy traffic without unnecessary weight, Dominator LP is easier to handle and install while maintaining dependable long-term performance. The result is a thoughtful balance of durability, design flexibility, and installation efficiency.",
    ],
    applications: [
      "Extremely durable carpet tile",
      "A lower-profile solution",
      "Full performance of Dominator in a lighter & thinner style",
    ],
    features: [
      "39 oz. Diagonal-patterned needle-punched carpet tile",
      "Lighter and thinner tile",
      "Premium (ECO) Bitumen backing",
    ],
  },
  "carpet-tile-goods/senator-softstep-plus": {
    productPdf: "/docs/carpet-tile/senator-softstep-plus_product-sheet.pdf",
    techSpecs: "/docs/carpet-tile/senator-softstep-plus_technical-specs.pdf",
    image: "/products/senator-softstep-plus/1_convenience-store.jpg",
    swatches: [
      { name: "Brown", src: "/products/senator-softstep-plus/swatches/brown.jpg" },
      { name: "Charcoal", src: "/products/senator-softstep-plus/swatches/charcoal.jpg" },
    ],
    gallery: [
      { src: "/products/senator-softstep-plus/1_convenience-store.jpg", label: "Retail service counter" },
      { src: "/products/senator-softstep-plus/2_pharmacy-aisle.jpg", label: "Pharmacy aisle" },
      { src: "/products/senator-softstep-plus/3_cross-section.jpg", label: "Cushioned foam backing cross-section" },
    ],
    description: [
      "Senator SoftStep™ Plus Carpet Tile brings warmth, softness, and thoughtful support to workplaces where employees spend long hours standing. Its velour-inspired surface creates a refined, professional appearance, while the cushioned foam backing helps soften each step and ease pressure on feet and legs throughout the day.",
      "Designed for pharmacies, service counters, workstations, and other continuously active environments, Senator SoftStep™ Plus is available in multiple tile sizes and engineered to meet Class 1 performance standards. The result is a versatile flooring solution that balances lasting comfort, tailored character, and dependable durability.",
    ],
    applications: [
      "Re-engineered to be a Class 1 product",
      "The warmth and resilience of velour carpeting",
      "A favorite in pharmacies for comfort",
    ],
    features: [
      "Easy to install and maintain",
      "Now available in 18\u201d x 18\u201d or 18\u201d x 36\u201d carpet tiles",
    ],
  },
  "carpet-tile-goods/champion-lp": {
    productPdf: "/docs/carpet-tile/champion-lp_product-sheet.pdf",
    techSpecs: "/docs/carpet-tile/champion-lp_technical-specs.pdf",
    image: "/products/champion-lp/0_grey-lobby.jpg",
    swatches: [
      { name: "Beige", src: "/products/champion-lp/swatches/beige.jpg" },
      { name: "Chocolate", src: "/products/champion-lp/swatches/chocolate.jpg" },
      { name: "Khaki", src: "/products/champion-lp/swatches/khaki.jpg" },
      { name: "Marble", src: "/products/champion-lp/swatches/marble.jpg" },
      { name: "Smokey Grey", src: "/products/champion-lp/swatches/smokey-grey.jpg" },
      { name: "Solid Crimson", src: "/products/champion-lp/swatches/solid-crimson.jpg" },
    ],
    gallery: [
      { src: "/products/champion-lp/0_grey-lobby.jpg", label: "Lobby entrance" },
      { src: "/products/champion-lp/2_fitness_v4.jpg", label: "Fitness center entrance" },
      { src: "/products/champion-lp/3_tile-detail.jpg", label: "Ribbed tile detail" },
    ],
    description: [
      "Champion LP brings purposeful texture and understated character to hardworking commercial entrances. Its classic ribbed surface creates a clean, linear pattern that helps guide moisture away while allowing captured dirt and debris to be easily swept or vacuumed.",
      "Crafted from 100% solution-dyed, UV-stabilized polypropylene and supported by a durable vinyl backing, Champion LP performs reliably across indoor and outdoor applications. Well suited for entryways and ramps, it offers a thoughtful balance of practical design, dependable floor protection, and lasting value.",
    ],
    applications: [
      "Lower profile than Champion XP",
      "Can withstand dirt and water",
      "Dense Berber tile resilient to crushing",
    ],
    features: [
      "Dense Berber tile features unmatched durability and flexibility",
    ],
  },
  "carpet-tile-goods/windsor": {
    productPdf: "/docs/carpet-tile/windsor-tile_product-sheet.pdf",
    techSpecs: "/docs/carpet-tile/windsor-tile_technical-specs.pdf",
    image: "/products/windsor-tile/open-office.jpg",
    gallery: [
      { src: "/products/windsor-tile/hallway.jpg", label: "Corporate corridor" },
      { src: "/products/windsor-tile/conference.jpg", label: "Conference room" },
      { src: "/products/windsor-tile/open-office.jpg", label: "Open office suite" },
    ],
    swatches: [
      { name: "Charcoal", src: "/products/windsor-tile/swatches/charcoal.jpg" },
      { name: "Dark Grey", src: "/products/windsor-tile/swatches/dark-grey.jpg" },
      { name: "Navy", src: "/products/windsor-tile/swatches/navy.jpg" },
      { name: "Red", src: "/products/windsor-tile/swatches/red.jpg" },
      { name: "Taupe", src: "/products/windsor-tile/swatches/taupe.jpg" },
    ],
    swatchCount: 5,
    description: [
      "Windsor Tiles bring natural rhythm and tailored refinement to active commercial interiors. Inspired by the delicate striations found in nature, their layered texture introduces warmth, movement, and quiet visual depth without overwhelming the surrounding design.",
      "Installed in a quarter-turn pattern, the tiles create a more expressive composition that highlights the material's subtle variation. Crafted for long-term performance in demanding, high-traffic spaces, Windsor balances sophisticated character, lasting comfort, and commercial durability in a versatile modular format.",
    ],
    applications: [
      "Designer striae pattern inspired by nature",
      "Premium style with commercial-grade durability",
      "Quarter turn installation for unique visual effects",
    ],
    features: [
      { label: "Face Weight", value: "30 oz. Designer Striae Nylon Carpet Tile" },
      { label: "Fiber Content", value: "100% PA6 Space Dyed Nylon" },
    ],
  },

  "broadloom-sheet-roll-goods/champion-super-nop": {
    productPdf: "/docs/broadlooms/champion-super-nop_product-sheet.pdf",
    techSpecs: "/docs/broadlooms/champion-super-nop_technical-specs.pdf",
    image: "/products/csn/waiting-chairs-v2.jpg",
    gallery: [
      { src: "/products/csn/waiting-chairs-v2.jpg", label: "Waiting area" },
      { src: "/products/csn/entry-security-v2.jpg", label: "Building entrance" },
      { src: "/products/csn/concert-hall.jpg", label: "Concert hall lobby" },
    ],
    swatches: [
      { name: "Autumn", src: "/products/csn/swatches/autumn.jpg" },
      { name: "Azure", src: "/products/csn/swatches/azure.jpg" },
      { name: "Black Shadow", src: "/products/csn/swatches/black-shadow.jpg" },
      { name: "Black", src: "/products/csn/swatches/black.jpg" },
      { name: "Bright Red", src: "/products/csn/swatches/bright-red.jpg" },
      { name: "Burgundy", src: "/products/csn/swatches/burgundy.jpg" },
      { name: "Cardinal Red", src: "/products/csn/swatches/cardinal-red.jpg" },
      { name: "Charcoal", src: "/products/csn/swatches/charcoal-2.jpg" },
      { name: "Chocolate", src: "/products/csn/swatches/chocolate.jpg" },
      { name: "Coir Natural", src: "/products/csn/swatches/coir-natural.jpg" },
      { name: "Crimson", src: "/products/csn/swatches/crimison.jpg" },
      { name: "Garnet", src: "/products/csn/swatches/garnet.jpg" },
      { name: "Gold", src: "/products/csn/swatches/gold.jpg" },
      { name: "Harvest", src: "/products/csn/swatches/harvest.jpg" },
      { name: "Hunter Green", src: "/products/csn/swatches/hunter-green.jpg" },
      { name: "Kelly Green", src: "/products/csn/swatches/kelly-green.jpg" },
      { name: "Loden", src: "/products/csn/swatches/loden.jpg" },
      { name: "Midnight Blue", src: "/products/csn/swatches/midnight-blue.jpg" },
      { name: "Natural Beige", src: "/products/csn/swatches/natural-beige.jpg" },
      { name: "Orange", src: "/products/csn/swatches/orange.jpg" },
      { name: "Pink", src: "/products/csn/swatches/pink.jpg" },
      { name: "Prism", src: "/products/csn/swatches/prisim.jpg" },
      { name: "Purple", src: "/products/csn/swatches/purple.jpg" },
      { name: "Rosewood", src: "/products/csn/swatches/rosewood.jpg" },
      { name: "Royal Blue", src: "/products/csn/swatches/royal-blue.jpg" },
      { name: "Sand", src: "/products/csn/swatches/sand.jpg" },
      { name: "Sky Blue", src: "/products/csn/swatches/sky-blue.jpg" },
      { name: "Solid Crimson", src: "/products/csn/swatches/solid-crimison.jpg" },
      { name: "Steel Blue", src: "/products/csn/swatches/steel-blue.jpg" },
      { name: "Sterling", src: "/products/csn/swatches/sterling.jpg" },
      { name: "Taupe", src: "/products/csn/swatches/taupe.jpg" },
      { name: "Teak", src: "/products/csn/swatches/teak.jpg" },
      { name: "Turquoise", src: "/products/csn/swatches/turquoise.jpg" },
      { name: "Verdi Green", src: "/products/csn/swatches/verdi-green.jpg" },
      { name: "Walnut", src: "/products/csn/swatches/walnut.jpg" },
      { name: "White", src: "/products/csn/swatches/white.jpg" },
      { name: "Yellow", src: "/products/csn/swatches/yellow.jpg" },
      { name: "Zinc", src: "/products/csn/swatches/zinc.jpg" },
    ],
    description: [
      "Champion Super Nop brings a refined, artisan character to hardworking entrance spaces. Its richly textured, non-woven surface is crafted from 100% ASOTA® polypropylene fibers, creating a resilient foundation for custom logo work and distinctive designs. Available in versatile roll goods, its carefully curated palette ranges from warm neutrals and timeless standards to expressive, fashion-forward colors.",
      "As our premier and most popular logo mat material, Champion Super Nop balances visual craftsmanship with dependable performance. It stands up beautifully to moisture, dirt, UV exposure, stains, and continuous foot traffic, helping protect interior floors while giving virtually any entrance a polished, thoughtfully designed finish.",
    ],
    applications: [
      "High-traffic commercial entrances",
      "Corporate offices and lobbies",
      "Retail and hospitality spaces",
      "Healthcare and education facilities",
    ],
    features: [
      { label: "Face Weight", value: "54 oz. All-Weather Berber Carpet" },
      { label: "Construction", value: "Non-woven / needle-punched" },
      { label: "Pattern", value: "Hobnail (berber) pattern" },
      { label: "Fiber Content", value: "100% Solution-dyed UV stabilized ASOTA® polypropylene" },
    ],
    swatchCount: 38,
  },
  "carpet-tile-goods/champion-super-nop-tile": {
    productPdf: "/docs/carpet-tile/champion-super-nop-tile_product-sheet.pdf",
    techSpecs: "/docs/carpet-tile/champion-super-nop-tile_technical-specs.pdf",
    image: "/products/csn-tile/entrance.jpg",
    gallery: [
      { src: "/products/csn-tile/office.jpg", label: "Corporate office" },
      { src: "/products/csn-tile/retail.jpg", label: "Retail space" },
      { src: "/products/csn-tile/grocery.jpg", label: "Grocery store" },
      { src: "/products/csn-tile/hardware.jpg", label: "Hardware store" },
      { src: "/products/csn-tile/entrance.jpg", label: "Commercial entrance" },
    ],
    swatches: [
      { name: "Autumn", src: "/products/csn-tile/swatches/autumn.jpg" },
      { name: "Azure", src: "/products/csn-tile/swatches/azure.jpg" },
      { name: "Black Shadow", src: "/products/csn-tile/swatches/black-shadow.jpg" },
      { name: "Cardinal Red", src: "/products/csn-tile/swatches/cardinal-red.jpg" },
      { name: "Charcoal", src: "/products/csn-tile/swatches/charcoal.jpg" },
      { name: "Chocolate", src: "/products/csn-tile/swatches/chocolate.jpg" },
      { name: "Coir", src: "/products/csn-tile/swatches/coir.jpg" },
      { name: "Garnet", src: "/products/csn-tile/swatches/garnet.jpg" },
      { name: "Hunter Green", src: "/products/csn-tile/swatches/hunter-green.jpg" },
      { name: "Loden", src: "/products/csn-tile/swatches/loden.jpg" },
      { name: "Midnight Blue", src: "/products/csn-tile/swatches/midnight-blue.jpg" },
      { name: "Natural Beige", src: "/products/csn-tile/swatches/natural-beige.jpg" },
      { name: "Prism", src: "/products/csn-tile/swatches/prism.jpg" },
      { name: "Rosewood", src: "/products/csn-tile/swatches/rosewood.jpg" },
      { name: "Steel Blue", src: "/products/csn-tile/swatches/steel-blue.jpg" },
      { name: "Sterling", src: "/products/csn-tile/swatches/sterling.jpg" },
      { name: "Taupe", src: "/products/csn-tile/swatches/taupe.jpg" },
      { name: "Teak", src: "/products/csn-tile/swatches/teak.jpg" },
      { name: "Verde Green", src: "/products/csn-tile/swatches/verde-green.jpg" },
      { name: "Walnut", src: "/products/csn-tile/swatches/walnut.jpg" },
      { name: "Zinc", src: "/products/csn-tile/swatches/zinc.jpg" },
    ],
    description: [
      "Champion Super Nop Tile brings artisan-inspired texture and thoughtful design to a versatile modular format. Its carefully curated palette, including warm neutrals, timeless standards, and expressive brights, offers the creative freedom to shape distinctive patterns, custom accents, and cohesive flooring installations.",
      "Crafted from our most popular logo mat material, each tile combines rich visual character with dependable all-weather performance. Its modular construction allows spaces to be designed, refreshed, and maintained with ease, making Champion Super Nop Tile a refined, durable choice for environments where craftsmanship, flexibility, and lasting performance matter equally.",
    ],
    applications: [
      "Dependable entryways in wet and high-traffic areas",
      "Wide range of colors for design flexibility",
      "All-weather performance and timeless design",
    ],
    features: [
      { label: "Face Weight", value: "54 oz. All-Weather Berber Carpet" },
      { label: "Construction", value: "Non-woven / needle-punched" },
      { label: "Pattern", value: "Hobnail (berber) pattern" },
      { label: "Fiber Content", value: "100% Solution-dyed UV stabilized ASOTA® polypropylene" },
    ],
    swatchCount: 21,
  },
  "custom-logos/natural-coco-custom": {
    productPdf: "/docs/custom-logos/natcoco-custom_product-sheet.pdf",
    techSpecs: "/docs/custom-logos/natcoco-custom_technical-specs.pdf",
    image: "/products/natcoco-custom/1_monogram.jpg",
    gallery: [
      { src: "/products/natcoco-custom/1_monogram.jpg", label: "Custom monogram border mat" },
      { src: "/products/natcoco-custom/2_logo.jpg", label: "Custom corporate logo mat" },
    ],
    swatches: [
      { name: "Natural", src: "/products/natcoco-custom/swatches/natural.jpg" },
      { name: "Black", src: "/products/natcoco-custom/swatches/black.jpg" },
      { name: "Brown", src: "/products/natcoco-custom/swatches/brown.jpg" },
      { name: "Grey", src: "/products/natcoco-custom/swatches/grey.jpg" },
      { name: "Green", src: "/products/natcoco-custom/swatches/green.jpg" },
      { name: "Blue", src: "/products/natcoco-custom/swatches/blue.jpg" },
      { name: "Red", src: "/products/natcoco-custom/swatches/red.jpg" },
    ],
    swatchCount: 7,
    description: [
      "Natural Coco brings authentic material character and organic warmth to thoughtfully designed entrances. Crafted from durable coconut coir fibers, its richly textured surface captures dirt and debris while offering a distinctive alternative to conventional synthetic matting.",
      "Designed specifically for recessed wells and commercial entryways, Natural Coco creates a welcoming transition into the space while supporting a more natural design sensibility. The result is a practical entrance mat shaped by honest materials, timeless texture, and understated craftsmanship.",
    ],
    applications: [
      "Made with natural coconut/coir fibers",
      "Provides an unparalleled premium look",
      "Natural look & feel for indoor applications",
    ],
    features: [
      "Material: Natural coconut/coir fibers",
      "Type: Premium sheet & roll goods",
    ],
  },
  "custom-logos/pinnacle-h-d": {
    productPdf: "/docs/custom-logos/pinnacle-hd_product-sheet.pdf",
    techSpecs: "/docs/custom-logos/pinnacle-hd_technical-specs.pdf",
    image: "/products/pinnacle-hd/1_restaurant.jpg",
    gallery: [
      { src: "/products/pinnacle-hd/1_restaurant.jpg", label: "Custom restaurant entrance logo" },
      { src: "/products/pinnacle-hd/2_drainage.jpg", label: "Inlaid design & drainage holes", fit: "contain" },
      { src: "/products/pinnacle-hd/3_profile.jpg", label: "1/4\u201d ~250 mils profile", fit: "contain" },
    ],
    swatches: [
      { name: "Black", src: "/products/pinnacle-hd/swatches/black.jpg" },
      { name: "Grey", src: "/products/pinnacle-hd/swatches/grey.jpg" },
      { name: "Brown", src: "/products/pinnacle-hd/swatches/brown.jpg" },
      { name: "Beige", src: "/products/pinnacle-hd/swatches/beige.jpg" },
      { name: "Red", src: "/products/pinnacle-hd/swatches/red.jpg" },
      { name: "Yellow", src: "/products/pinnacle-hd/swatches/yellow.jpg" },
      { name: "Green", src: "/products/pinnacle-hd/swatches/green.jpg" },
      { name: "Blue", src: "/products/pinnacle-hd/swatches/blue.jpg" },
      { name: "Light Blue", src: "/products/pinnacle-hd/swatches/light-blue.jpg" },
      { name: "White", src: "/products/pinnacle-hd/swatches/white.jpg" },
    ],
    swatchCount: 10,
    description: [
      "Pinnacle H.D. PVC vinyl logo mats bring bold visual precision and enduring material character to entrances exposed to heavy traffic and extreme climates. Available in a wide range of colors, they provide creative flexibility to translate distinctive branding into a durable architectural feature.",
      "Each design is individually precision-cut and carefully inlaid into the mat rather than printed onto its surface. This crafted construction allows the image to remain an integral part of the material, delivering lasting color, definition, and branded impact throughout the life of the mat.",
    ],
    applications: [
      "Fully ADA Compliant",
      "Extremely strong & tough",
      "Aggressive scraping",
    ],
    features: [
      "Compliance: Fully ADA Compliant",
      "Durability: UV Resistant, all-weather performance",
    ],
  },
  "custom-logos/synthetic-coco-custom": {
    productPdf: "/docs/custom-logos/syncoco-custom_product-sheet.pdf",
    techSpecs: "/docs/custom-logos/syncoco-custom_technical-specs.pdf",
    image: "/products/syncoco-custom/1_lobby.jpg",
    gallery: [
      { src: "/products/syncoco-custom/1_lobby.jpg", label: "Custom hotel lobby logo mat" },
      { src: "/products/syncoco-custom/2_welcome.jpg", label: "Decorative welcome entrance mat" },
    ],
    swatches: [
      { name: "Natural", src: "/products/syncoco-custom/swatches/natural.jpg" },
      { name: "Grey", src: "/products/syncoco-custom/swatches/grey.jpg" },
      { name: "Black", src: "/products/syncoco-custom/swatches/black.jpg" },
    ],
    swatchCount: 3,
    description: [
      "Synthetic Coco Logo Mats pair the warm, organic character of natural coir with the precision of custom-branded design. Crafted from 100% solution-dyed, UV-stabilized polypropylene fibers, their richly textured surface resists stains and fading while providing a cleaner, shed-free alternative to traditional coco matting.",
      "Quick-drying and designed for medium- to high-traffic entrances, the material can be precisely cut into custom logo shapes up to 12 feet. The result is a distinctive entrance feature that combines natural-inspired texture, lasting performance, and a thoughtfully crafted expression of your brand.",
    ],
    applications: [
      "100% solution-dyed UV-stabilized polypropylene fibers are stain and fade resistant",
      "Dries quickly when compared to natural cocoa mats",
    ],
    features: [
      "Can be cut into any shape for custom mats up to 12\u2019",
      "Functions best in medium to high-traffic locations, both indoor or outdoor",
    ],
  },
  "custom-logos/windsor": {
    productPdf: "/docs/custom-logos/windsor-custom_product-sheet.pdf",
    techSpecs: "/docs/custom-logos/windsor-custom_technical-specs.pdf",
    image: "/products/windsor-custom/1_lobby.jpg",
    gallery: [
      { src: "/products/windsor-custom/1_lobby.jpg", label: "Grand hotel lobby entrance" },
      { src: "/products/windsor-custom/2_logo.jpg", label: "Custom branded logo inlay" },
    ],
    swatches: [
      { name: "Charcoal Black", src: "/products/windsor-custom/swatches/charcoal-black.jpg" },
      { name: "Dark Grey", src: "/products/windsor-custom/swatches/dark-grey.jpg" },
      { name: "Taupe", src: "/products/windsor-custom/swatches/taupe.jpg" },
      { name: "Navy", src: "/products/windsor-custom/swatches/navy.jpg" },
      { name: "Red", src: "/products/windsor-custom/swatches/red.jpg" },
    ],
    swatchCount: 5,
    description: [
      "Windsor Customs brings tailored elegance and natural movement to commercial spaces designed to make a lasting impression. Its subtly striated surface creates warmth and visual depth, providing a refined foundation for custom artwork and distinctive branded designs.",
      "Crafted to retain its character beneath heavy foot traffic, Windsor Customs balances sophisticated styling with dependable commercial performance. The result is a carefully composed custom flooring feature that keeps entrances and interiors attractive, functional, and inviting for years to come.",
    ],
    applications: [
      "Beautiful, striated design available in five colors",
      "Tufted 30 oz nylon provides superior performance",
      "Available in carpet tile & sheet/roll goods",
    ],
    features: [
      "Type: Premium sheet and roll goods",
      "Pattern: Nature-inspired striae pattern",
      "Aesthetic: Warm, modern aesthetic",
    ],
  },
  "custom-logos/champion-super-nop-csn-customs": {
    productPdf: "/docs/custom-logos/csn-custom_product-sheet.pdf",
    techSpecs: "/docs/custom-logos/csn-custom_technical-specs.pdf",
    image: "/products/csn-customs/reception.jpg",
    gallery: [
      { src: "/products/csn-customs/entrance-warriors.jpg", label: "Custom logo entrance" },
      { src: "/products/csn-customs/residence.jpg", label: "Residential porch" },
      { src: "/products/csn-customs/office.jpg", label: "Corporate lobby" },
      { src: "/products/csn-customs/reception.jpg", label: "Reception logo mat" },
      { src: "/products/csn-customs/excellence-pacesferry.jpg", label: "Hospitality lounge" },
    ],
    swatches: [
      { name: "Autumn", src: "/products/csn/swatches/autumn.jpg" },
      { name: "Azure", src: "/products/csn/swatches/azure.jpg" },
      { name: "Black Shadow", src: "/products/csn/swatches/black-shadow.jpg" },
      { name: "Black", src: "/products/csn/swatches/black.jpg" },
      { name: "Bright Red", src: "/products/csn/swatches/bright-red.jpg" },
      { name: "Burgundy", src: "/products/csn/swatches/burgundy.jpg" },
      { name: "Cardinal Red", src: "/products/csn/swatches/cardinal-red.jpg" },
      { name: "Charcoal", src: "/products/csn/swatches/charcoal-2.jpg" },
      { name: "Chocolate", src: "/products/csn/swatches/chocolate.jpg" },
      { name: "Coir Natural", src: "/products/csn/swatches/coir-natural.jpg" },
      { name: "Crimson", src: "/products/csn/swatches/crimison.jpg" },
      { name: "Garnet", src: "/products/csn/swatches/garnet.jpg" },
      { name: "Gold", src: "/products/csn/swatches/gold.jpg" },
      { name: "Harvest", src: "/products/csn/swatches/harvest.jpg" },
      { name: "Hunter Green", src: "/products/csn/swatches/hunter-green.jpg" },
      { name: "Kelly Green", src: "/products/csn/swatches/kelly-green.jpg" },
      { name: "Loden", src: "/products/csn/swatches/loden.jpg" },
      { name: "Midnight Blue", src: "/products/csn/swatches/midnight-blue.jpg" },
      { name: "Natural Beige", src: "/products/csn/swatches/natural-beige.jpg" },
      { name: "Orange", src: "/products/csn/swatches/orange.jpg" },
      { name: "Pink", src: "/products/csn/swatches/pink.jpg" },
      { name: "Prism", src: "/products/csn/swatches/prisim.jpg" },
      { name: "Purple", src: "/products/csn/swatches/purple.jpg" },
      { name: "Rosewood", src: "/products/csn/swatches/rosewood.jpg" },
      { name: "Royal Blue", src: "/products/csn/swatches/royal-blue.jpg" },
      { name: "Sand", src: "/products/csn/swatches/sand.jpg" },
      { name: "Sky Blue", src: "/products/csn/swatches/sky-blue.jpg" },
      { name: "Solid Crimson", src: "/products/csn/swatches/solid-crimison.jpg" },
      { name: "Steel Blue", src: "/products/csn/swatches/steel-blue.jpg" },
      { name: "Sterling", src: "/products/csn/swatches/sterling.jpg" },
      { name: "Taupe", src: "/products/csn/swatches/taupe.jpg" },
      { name: "Teak", src: "/products/csn/swatches/teak.jpg" },
      { name: "Turquoise", src: "/products/csn/swatches/turquoise.jpg" },
      { name: "Verdi Green", src: "/products/csn/swatches/verdi-green.jpg" },
      { name: "Walnut", src: "/products/csn/swatches/walnut.jpg" },
      { name: "White", src: "/products/csn/swatches/white.jpg" },
      { name: "Yellow", src: "/products/csn/swatches/yellow.jpg" },
      { name: "Zinc", src: "/products/csn/swatches/zinc.jpg" },
    ],
    description: [
      "Champion Super Nop brings artisan texture and enduring character to entrances designed to make a lasting impression. Its richly crafted non-woven surface is made from 100% ASOTA® polypropylene fibers, creating a resilient material that retains its appearance through heavy foot traffic, changing weather, and everyday use.",
      "A thoughtfully curated palette of warm neutrals, modern brights, and classic colors allows Champion Super Nop to complement virtually any architectural setting. UV stable, stain resistant, and naturally resilient, it offers a refined balance of visual craftsmanship and all-weather performance, protecting interior floors while maintaining a polished, professional entrance with minimal upkeep.",
    ],
    applications: [
      "Versatility in wet and high-traffic areas",
      "Logo or plain matting options in a wide variety of colors",
      "Provides extra-high dirt absorption",
    ],
    features: [
      { label: "Details", value: "Corners patched for enforcement" },
      { label: "Construction", value: "Non-woven / needle-punched" },
      { label: "Fiber Content", value: "100% Solution-dyed UV stabilized ASOTA® polypropylene" },
    ],
  },
  "luxury-vinyl-plank-tiles/natures-view-style-rustique-7-x-48": {
    certifications: [
      { src: "/certifications/floorscore.svg", alt: "FloorScore SCS Certified" },
      { src: "/certifications/greenguard-gold.svg", alt: "UL GREENGUARD Gold Certified" },
      { src: "/certifications/eurofins-iac.svg", alt: "Eurofins Indoor Air Comfort" },
      { src: "/certifications/anab.svg", alt: "ANAB Accredited" },
      { src: "/certifications/1k-protective-coating.png", alt: "1K Protective Coating" },
    ],
    productPdf: "/docs/lvpt/nvr-7x48_product-sheet.pdf",
    techSpecs: "/docs/lvpt/nvr-7x48_technical-specs.pdf",
    image: "/products/nv-7x48/toasted-oak.jpg",
    gallery: [
      { src: "/products/nv-7x48/toasted-oak.jpg", label: "Toasted Oak" },
      { src: "/products/nv-7x48/silver-sand.jpg", label: "Silver Sand" },
      { src: "/products/nv-7x48/sundried-teak.jpg", label: "Sundried Teak" },
      { src: "/products/nv-7x48/sterling-ash.jpg", label: "Sterling Ash" },
      { src: "/products/nv-7x48/dove.jpg", label: "Dove" },
    ],
    swatches: [
      { name: "African Blackwood", src: "/products/nv-7x48/swatches/african-blackwood.jpg" },
      { name: "Anchor Grey", src: "/products/nv-7x48/swatches/anchor-grey.jpg" },
      { name: "Antique Brown", src: "/products/nv-7x48/swatches/antique-brown.jpg" },
      { name: "Ashwood Grey", src: "/products/nv-7x48/swatches/ashwood-grey.jpg" },
      { name: "Canyon Dust Cross Cut", src: "/products/nv-7x48/swatches/canyon-dust-cross-cut.jpg" },
      { name: "Cape Cod Grey", src: "/products/nv-7x48/swatches/cape-cod-grey.jpg" },
      { name: "Dove", src: "/products/nv-7x48/swatches/dove.jpg" },
      { name: "Harvest Oak", src: "/products/nv-7x48/swatches/harvest-oak.jpg" },
      { name: "Honduran Rosewood", src: "/products/nv-7x48/swatches/honduran-rosewood.jpg" },
      { name: "Ice Frost Oak", src: "/products/nv-7x48/swatches/ice-frost-oak.jpg" },
      { name: "Neutral Beige", src: "/products/nv-7x48/swatches/neutral-beige.jpg" },
      { name: "Oak Barrel", src: "/products/nv-7x48/swatches/oak-barrel.jpg" },
      { name: "Silver Sand", src: "/products/nv-7x48/swatches/silver-sand.jpg" },
      { name: "Smoked Walnut", src: "/products/nv-7x48/swatches/smoked-walnut.jpg" },
      { name: "Sterling Ash", src: "/products/nv-7x48/swatches/sterling-ash.jpg" },
      { name: "Sundried Teak", src: "/products/nv-7x48/swatches/sundried-teak.jpg" },
      { name: "Toasted Oak", src: "/products/nv-7x48/swatches/toasted-oak.jpg" },
      { name: "Weathered Ash", src: "/products/nv-7x48/swatches/weathered-ash.jpg" },
      { name: "Wheatfields", src: "/products/nv-7x48/swatches/wheatfields.jpg" },
      { name: "Whiskey Cross Cut", src: "/products/nv-7x48/swatches/whiskey-cross-cut.jpg" },
      { name: "Windswept Beige", src: "/products/nv-7x48/swatches/windswept-beige.jpg" },
    ],
    description: [
      "Natures View™ Style Rustique 7\" x 48\" captures the warmth, depth, and distinctive character of natural wood in a beautifully crafted luxury vinyl plank. Its generous proportions and carefully rendered grain details create an authentic, artisan-inspired surface that brings a sense of comfort and refinement to commercial interiors.",
      "Designed for demanding, high-traffic spaces, Style Rustique preserves its beauty without the maintenance, vulnerability, or downtime associated with real wood. The result is a thoughtful balance of natural artistry and commercial strength, giving you the character of wood with the durability and lasting confidence your space requires.",
    ],
    applications: [
      "Natural look and feel",
      "Designed for heavy traffic",
      "Antibacterial",
    ],
    features: [
      { label: "Overall Thickness", value: '3.175mm | 0.125"' },
      { label: "Wear Layer Thickness", value: "1.0mm | 40mil" },
      { label: "Plank Size", value: '7" x 48"' },
      { label: "Static Load", value: "2000 psi (ASTM F970 - Modified)" },
    ],
    swatchCount: 21,
  },
  "luxury-vinyl-plank-tiles/natures-view-style-rustique-18-x-18": {
    certifications: [
      { src: "/certifications/floorscore.svg", alt: "FloorScore SCS Certified" },
      { src: "/certifications/greenguard-gold.svg", alt: "UL GREENGUARD Gold Certified" },
      { src: "/certifications/eurofins-iac.svg", alt: "Eurofins Indoor Air Comfort" },
      { src: "/certifications/anab.svg", alt: "ANAB Accredited" },
      { src: "/certifications/1k-protective-coating.png", alt: "1K Protective Coating" },
    ],
    productPdf: "/docs/lvpt/nvr-18x18_product-sheet.pdf",
    techSpecs: "/docs/lvpt/nvr-18x18_technical-specs.pdf",
    image: "/products/nv-18x18/grocery.jpg",
    gallery: [
      { src: "/products/nv-18x18/grocery.jpg", label: "Grocery store" },
      { src: "/products/nv-18x18/office.jpg", label: "Corporate office" },
      { src: "/products/nv-18x18/bakery-v2.jpg", label: "Bakery department" },
      { src: "/products/nv-18x18/aisles.jpg", label: "Retail aisles" },
    ],
    swatches: [
      { name: "Foundation Grey", src: "/products/nv-18x18/swatches/foundation-grey.jpg" },
      { name: "Limestone", src: "/products/nv-18x18/swatches/limestone.jpg" },
      { name: "Metallic Delight", src: "/products/nv-18x18/swatches/metallic-delight.jpg" },
      { name: "Mink Slate", src: "/products/nv-18x18/swatches/mink-slate.jpg" },
      { name: "Morning Fog", src: "/products/nv-18x18/swatches/morning-fog.jpg" },
      { name: "Neutral Ground", src: "/products/nv-18x18/swatches/neutral-ground.jpg" },
      { name: "Ostrich Grey", src: "/products/nv-18x18/swatches/ostrich-grey.jpg" },
      { name: "Oxidized Pewter", src: "/products/nv-18x18/swatches/oxidized-pewter.jpg" },
      { name: "Oyster", src: "/products/nv-18x18/swatches/oyster.jpg" },
      { name: "River Rock", src: "/products/nv-18x18/swatches/river-rock.jpg" },
      { name: "Silver Knight", src: "/products/nv-18x18/swatches/silver-knight.jpg" },
      { name: "Titanium", src: "/products/nv-18x18/swatches/titanium.jpg" },
      { name: "Toasted Almond", src: "/products/nv-18x18/swatches/toasted-almond.jpg" },
      { name: "Urban Grey", src: "/products/nv-18x18/swatches/urban-grey.jpg" },
    ],
    description: [
      "Natures View™ Style Rustique 18\" x 18\" captures the depth, variation, and enduring character of natural stone in a beautifully crafted luxury vinyl tile. Available in 14 distinctive styles, its refined surface detailing brings an artisan quality to commercial interiors, without the weight, maintenance, or installation limitations of genuine stone.",
      "Designed for demanding environments, Style Rustique combines natural-looking artistry with dependable commercial performance. The result is a versatile floor that offers the beauty of stone, the resilience of luxury vinyl, and lasting confidence in spaces made to be experienced every day.",
    ],
    applications: [
      "Natural look and feel",
      "Designed for heavy traffic",
      "Antibacterial",
    ],
    features: [
      { label: "Overall Thickness", value: '3.175mm | 0.125"' },
      { label: "Wear Layer Thickness", value: "1.0mm | 40mil" },
      { label: "Tile Size", value: '18" x 18"' },
      { label: "Static Load", value: "2000 psi (ASTM F970 - Modified)" },
    ],
    swatchCount: 14,
  },
  "luxury-vinyl-plank-tiles/natures-view-style-rustique-18-x-36": {
    certifications: [
      { src: "/certifications/floorscore.svg", alt: "FloorScore SCS Certified" },
      { src: "/certifications/greenguard-gold.svg", alt: "UL GREENGUARD Gold Certified" },
      { src: "/certifications/eurofins-iac.svg", alt: "Eurofins Indoor Air Comfort" },
      { src: "/certifications/anab.svg", alt: "ANAB Accredited" },
      { src: "/certifications/1k-protective-coating.png", alt: "1K Protective Coating" },
    ],
    productPdf: "/docs/lvpt/nvr-18x36_product-sheet.pdf",
    techSpecs: "/docs/lvpt/nvr-18x36_technical-specs.pdf",
    image: "/products/nv-18x36/1_office.jpg",
    gallery: [
      { src: "/products/nv-18x36/1_office.jpg", label: "Corporate office" },
      { src: "/products/nv-18x36/2_restaurant.jpg", label: "Restaurant dining" },
    ],
    description: [
      "Natures View™ Style Rustique Tile 18” x 36” captures the depth, variation, and timeless character of natural stone in a thoughtfully crafted, more practical form. Its refined surface detailing brings an artisan quality to commercial interiors without the cost, weight, installation demands, or ongoing maintenance associated with genuine stone.",
      "Designed to retain its beauty in high-traffic environments, Style Rustique combines authentic stone-inspired artistry with dependable durability and easy care, giving you the freedom to create sophisticated spaces with lasting confidence.",
    ],
    applications: [
      "Natural look and feel",
      "Designed for heavy traffic",
      "Antibacterial",
    ],
    features: [
      { label: "Overall Thickness", value: '3.175mm | 0.125"' },
      { label: "Wear Layer Thickness", value: "1.0mm | 40mil" },
      { label: "Tile Size", value: '18" x 36"' },
      { label: "Static Load", value: "2000 psi (ASTM F970 - Modified)" },
    ],
    swatches: [
      { name: "Foundation Grey", src: "/products/nv-18x36/swatches/foundation-grey.jpg" },
      { name: "Morning Fog", src: "/products/nv-18x36/swatches/morning-fog.jpg" },
      { name: "Neutral Ground", src: "/products/nv-18x36/swatches/neutral-ground.jpg" },
      { name: "Silver Knight", src: "/products/nv-18x36/swatches/silver-knight.jpg" },
      { name: "Titanium", src: "/products/nv-18x36/swatches/titanium.jpg" },
    ],
  },
  "broadloom-sheet-roll-goods/eagle": {
    productPdf: "/docs/broadlooms/eagle_product-sheet.pdf",
    techSpecs: "/docs/broadlooms/eagle_technical-specs.pdf",
    image: "/products/eagle/1_grocery.jpg",
    gallery: [
      { src: "/products/eagle/1_grocery.jpg", label: "Retail counter" },
      { src: "/products/eagle/4_img4979.jpg", label: "Corporate lobby" },
      { src: "/products/eagle/3_img4977.jpg", label: "Government lobby" },
      { src: "/products/eagle/2_img1182.jpg", label: "Entrance threshold" },
    ],
    swatches: [
      { name: "Anthracite", src: "/products/eagle/swatches/anthracite.jpg" },
      { name: "Aspen", src: "/products/eagle/swatches/aspen.jpg" },
      { name: "Azure", src: "/products/eagle/swatches/azure.jpg" },
      { name: "Beige", src: "/products/eagle/swatches/beige.jpg" },
      { name: "Black Shadow", src: "/products/eagle/swatches/black-shadow.jpg" },
      { name: "Bordeaux", src: "/products/eagle/swatches/bordeaux.jpg" },
      { name: "Chocolate", src: "/products/eagle/swatches/chocolate.jpg" },
      { name: "Harvest", src: "/products/eagle/swatches/harvest.jpg" },
      { name: "Midnight Blue", src: "/products/eagle/swatches/midnight-blue.jpg" },
      { name: "Sisal", src: "/products/eagle/swatches/sisal.jpg" },
      { name: "Sterling", src: "/products/eagle/swatches/sterling.jpg" },
    ],
    description: [
      "Eagle brings a refined, textural quality to hardworking commercial entrances. Its durable fiber-bonded Berber surface is thoughtfully constructed to capture grit, moisture, and everyday debris before they travel farther into the building, helping preserve the beauty and condition of surrounding floors.",
      "Designed with a high-heel-friendly profile, Eagle performs comfortably and reliably in busy entryways. Its distinctive woven character, dependable dirt-trapping performance, and practical value make it a well-crafted entrance solution that balances understated style with lasting protection.",
    ],
    applications: [
      "High-traffic commercial entrances",
      "Corporate offices and lobbies",
      "Retail and hospitality spaces",
      "Healthcare and education facilities",
    ],
    features: [
      "Made with 100% ASOTA® polypropylene fibers",
      "UV stable, stain resistant",
      "Durable and a highly resilient non-woven carpet",
    ],
    swatchCount: 12,
  },
  "custom-logos/spectra-print-h-d": {
    productPdf: "/docs/custom-logos/spectra-print_product-sheet.pdf",
    techSpecs: "/docs/custom-logos/spectra-print_technical-specs.pdf",
    hideSwatches: true,
    image: "/products/spectra/waiting.jpg",
    gallery: [
      { src: "/products/spectra/waiting.jpg", label: "Corporate reception" },
      { src: "/products/spectra/reception.jpg", label: "Hospitality lobby" },
      { src: "/products/spectra/restaurant.jpg", label: "Cafe entrance" },
      { src: "/products/spectra/detailed.jpg", label: "Photographic detail" },
      { src: "/products/spectra/layers.jpg", label: "Mat construction" },
    ],
    description: [
      "Spectra Print HD transforms a commercial entrance into a finely crafted expression of your brand. Using advanced custom-printing technology and an extraordinary 2800 DPI resolution, it reproduces intricate artwork, refined details, and rich color with photographic clarity.",
      "Every line, tone, and visual element is rendered with remarkable precision, allowing your identity to appear as thoughtfully composed on the floor as it does in print. The result is more than a custom mat, it is a polished branded feature designed to make an immediate and lasting impression.",
    ],
    applications: [
      "High-traffic commercial entrances",
      "Corporate offices and lobbies",
      "Retail and hospitality spaces",
      "Healthcare and education facilities",
    ],
    features: [
      "Made in the USA",
      "Infinite Design Possibilities",
      "No minimums",
      "Resolution: 2800 DPI photographic-quality graphics",
      "Backing: Premium Rubber",
    ],
  },
  "entry-grid-systems/frontrunner-sb": {
    productPdf: "/docs/entry-grid/frontrunner-sb_product-sheet.pdf",
    techSpecs: "/docs/entry-grid/frontrunner-sb_technical-specs.pdf",
    image: "/products/frontrunner-sb/1_entrance.jpg",
    gallery: [
      { src: "/products/frontrunner-sb/1_entrance.jpg", label: "Facility entrance install" },
      { src: "/products/frontrunner-sb/2_surface.jpg", label: "Open-grid scraper surface" },
      { src: "/products/frontrunner-sb/3_profile.jpg", label: "1/4\u201d profile & weight" },
    ],
    swatches: [
      { name: "Black", src: "/products/frontrunner-sb/swatches/black.jpg" },
      { name: "Grey", src: "/products/frontrunner-sb/swatches/grey.jpg" },
    ],
    swatchCount: 2,
    description: [
      "This entrance system brings purposeful engineering to the first line of floor protection. Its closely spaced, open-grid structure captures dirt and debris beneath the walking surface, helping prevent contaminants from traveling farther into the building.",
      "Paired with Frontrunner Plus for added moisture removal, it creates a complete, permanent entrance solution with certified slip resistance. The result is a durable, thoughtfully integrated system that supports cleaner interiors, safer movement, and lower everyday maintenance.",
    ],
    applications: [
      "Suitable for heavy traffic areas",
      "Wheeled traffic friendly including shopping trolleys",
      "Open grid design lets debris fall through",
    ],
    features: [
      "Material: Flexible PVC",
      "Roll Length: Standard roll length 33 ft",
    ],
  },
  "entry-grid-systems/heron-rib": {
    productPdf: "/docs/entry-grid/heron-rib_product-sheet.pdf",
    techSpecs: "/docs/entry-grid/heron-rib_technical-specs.pdf",
    image: "/products/heron-rib/1_pool.jpg",
    gallery: [
      { src: "/products/heron-rib/1_pool.jpg", label: "Hotel indoor pool deck" },
      { src: "/products/heron-rib/2_locker.jpg", label: "Locker room & shower area" },
      { src: "/products/heron-rib/3_facility.jpg", label: "Aquatic facility walkway" },
    ],
    swatches: [
      { name: "Ocean Blue", src: "/products/heron-rib/swatches/ocean-blue.jpg" },
      { name: "Oxford Blue", src: "/products/heron-rib/swatches/oxford-blue.jpg" },
      { name: "Forest Green", src: "/products/heron-rib/swatches/forest-green.jpg" },
      { name: "Charcoal Grey", src: "/products/heron-rib/swatches/charcoal-grey.jpg" },
      { name: "Mulberry Red", src: "/products/heron-rib/swatches/mulberry-red.jpg" },
      { name: "White", src: "/products/heron-rib/swatches/white.jpg" },
    ],
    swatchCount: 6,
    swatchNote: "Note: Please expect longer lead times for this product.",
    description: [
      "Heron Rib brings sculpted texture and purposeful drainage to leisure and sports environments shaped by constant moisture. Crafted from strong, non-porous PVC, its embossed ribbed surface provides secure footing for barefoot traffic, while channeled underbars allow water to move quickly beneath the walking surface.",
      "Flexible enough to follow uneven contours and be cut for custom spaces, Heron Rib adapts naturally to pools, saunas, spas, and other wet zones. Its durable, UV-resistant construction balances clean material design with reliable drainage, lasting performance, and everyday slip protection.",
    ],
    applications: [
      "Perfect for extensive barefoot traffic",
      "UV resistant",
    ],
    features: [
      "Strong non-porous PVC with channeled under-bars",
      "Easy to cut and fit; contours to uneven surfaces",
    ],
  },
  "entry-grid-systems/heron-tile": {
    productPdf: "/docs/entry-grid/heron-tile_product-sheet.pdf",
    techSpecs: "/docs/entry-grid/heron-tile_technical-specs.pdf",
    image: "/products/heron-tile/1_poolside.jpg",
    gallery: [
      { src: "/products/heron-tile/1_poolside.jpg", label: "Poolside wet-area walkway" },
      { src: "/products/heron-tile/2_locker.jpg", label: "Locker room & shower area" },
    ],
    swatches: [
      { name: "Ocean Blue", src: "/products/heron-tile/swatches/ocean-blue.jpg" },
      { name: "Light Blue", src: "/products/heron-tile/swatches/light-blue.jpg" },
      { name: "Light Grey", src: "/products/heron-tile/swatches/light-grey.jpg" },
      { name: "Beige", src: "/products/heron-tile/swatches/beige.jpg" },
    ],
    swatchCount: 4,
    swatchNote: "Note: Please expect longer lead times for this product.",
    description: [
      "Heron Tile brings sculpted texture and supportive comfort to wet environments designed for barefoot movement. Crafted from resilient, 5/8-inch-thick EVA, its cushioned surface feels comfortable underfoot while the open construction allows water to drain away efficiently. An embossed finish provides added grip around pools, gyms, and leisure facilities.",
      "Built-in antimicrobial and antifungal additives support a cleaner, more hygienic surface. The interlocking tile format installs quickly and forms a cohesive floor, balancing thoughtful material design with dependable drainage, comfort, and everyday safety.",
    ],
    applications: [
      "Tiles easily click into place for simple installation",
      "Eases cleaning with excellent drainage",
    ],
    features: [
      "Material: Flexible PVC",
      "Thickness: 5/8\u201d cushioned EVA construction",
      "Embossed, antimicrobial & antifungal surface for wet areas",
    ],
  },
  "entry-grid-systems/snap-trax-tile": {
    productPdf: "/docs/entry-grid/snap-trax-tile_product-sheet.pdf",
    techSpecs: "/docs/entry-grid/snap-trax-tile_technical-specs.pdf",
    image: "/products/snaptrax/entrance-burlington.jpg",
    gallery: [
      { src: "/products/snaptrax/1_entrance.jpg", label: "Retail vestibule" },
      { src: "/products/snaptrax/4_retail.jpg", label: "Store entrance" },
      { src: "/products/snaptrax/5_sports.jpg", label: "Sporting goods entry" },
      { src: "/products/snaptrax/3_entrance.jpg", label: "Exit corridor" },
      { src: "/products/snaptrax/2_entrance.jpg", label: "Entry threshold" },
    ],
    swatches: [
      { name: "Black", src: "/products/snaptrax/swatches/black.jpg" },
    ],
    swatchCount: 1,
    description: [
      "Snap Trax Tile brings purposeful design and precision engineering to the first line of entrance protection. Its sculpted surface actively scrapes dirt, mud, and initial moisture from footwear, capturing debris before it can travel farther into the building.",
      "Crafted from recyclable, UV-resistant 100% TPE, each tile interlocks to create a strong, cohesive surface with a clean architectural character. Its dimensionally stable construction resists shrinking and gapping, while the modular format allows the system to adapt naturally to the space. Designed for high-traffic vestibules, Snap Trax Tile balances thoughtful material design with lasting strength and dependable performance.",
    ],
    applications: [
      "High-traffic commercial entrances",
      "Modular interlocking entry system",
      "Scrapes and controls dirt at the door",
      "Dimensionally stable - will not shrink or gap",
    ],
    features: [
      "Construction: 100% TPE",
      "Capable of handling 1,000 lb. rolling loads",
      "Material: Dimensionally stable TPE compound",
      "Interlocking 50cm x 50cm tiles allow versatility & customization for virtually any vestibule.",
    ],
  },
  "commercial-mats/tidal-diamond-mat": {
    productPdf: "/docs/commercial-mats/tidal-diamond_product-sheet.pdf",
    techSpecs: "/docs/commercial-mats/tidal-diamond_technical-specs.pdf",
    image: "/products/tidal/1_diamond.jpg",
    gallery: [
      { src: "/products/tidal/1_diamond.jpg", label: "Diamond pattern detail" },
      { src: "/products/tidal/2_interior.jpg", label: "Corporate lobby" },
      { src: "/products/tidal/3_exterior.jpg", label: "Building entrance" },
    ],
    swatches: [
      { name: "Brown", src: "/products/tidal/swatches/brown.jpg" },
      { name: "Burgundy", src: "/products/tidal/swatches/burgundy.jpg" },
      { name: "Charcoal", src: "/products/tidal/swatches/charcoal.jpg" },
      { name: "Evergreen", src: "/products/tidal/swatches/evergreen.jpg" },
      { name: "Grey", src: "/products/tidal/swatches/grey.jpg" },
      { name: "Navy", src: "/products/tidal/swatches/navy.jpg" },
      { name: "Red", src: "/products/tidal/swatches/red.jpg" },
    ],
    swatchCount: 7,
    description: [
      "Tidal Mat brings sculpted form and purposeful performance to entrances shaped by wet weather and heavy foot traffic. Its deeply patterned surface is thoughtfully engineered to scrape away dirt and snow while collecting moisture before it can travel onto surrounding floors.",
      "Beveled edges create a clean, finished profile, while Grip-Nobs along the backing help keep the mat securely positioned on both carpet and hard flooring. Designed to protect interior surfaces and help reduce slip hazards, Tidal Mat can also be customized with a logo, transforming practical entrance protection into a polished extension of your brand.",
    ],
    applications: [
      "High-traffic commercial entrances",
      "Corporate offices and lobbies",
      "Retail and hospitality spaces",
      "Helps maintain indoor floor covering",
    ],
    features: [
      "Beveled edging traps dirt and water",
      "Prevents slip hazards",
      "Water trapping system holds up to 1½ gallons of water per square yard",
      "Backed with Grip-Nobs that keep the mat from moving over carpet and improves traction on hard surfaces",
    ],
  },
  "commercial-mats/supertac-matting": {
    productPdf: "/docs/commercial-mats/supertac_product-sheet.pdf",
    techSpecs: "/docs/commercial-mats/supertac_technical-specs.pdf",
    image: "/products/supertac/1_floral.jpg",
    gallery: [
      { src: "/products/supertac/1_floral.jpg", label: "Retail floral display" },
      { src: "/products/supertac/2_bakery.jpg", label: "Bakery service counter" },
      { src: "/products/supertac/3_roll.jpg", label: "Self-adhering recyclable roll" },
    ],
    swatches: [
      { name: "Black", src: "/products/supertac/swatches/black.jpg" },
    ],
    swatchCount: 1,
    description: [
      "SuperTac Matting brings clean, purposeful design to high-risk areas exposed to water, oil, and constant movement. Its self-adhering construction creates a secure, low-profile surface that remains firmly in place beneath heavy cart traffic while capturing moisture and helping surrounding floors stay drier and safer.",
      "Made from 100% recyclable, disposable materials, SuperTac offers a practical approach to temporary floor protection and routine maintenance. Easy to place and replace, it balances dependable slip-and-fall prevention with straightforward care and responsible material use.",
    ],
    applications: [
      "Self-adhering, hassle-free solution",
      "Moisture barrier prevents liquids from soaking through",
      "Made from 100% recyclable and disposable materials",
    ],
    features: [
      "Type: Fast Installing indoor matting",
      "Material: 100% Post-Consumer Recycled Polyester Fabric",
      "Treatment: Odor Resistant - Anti-microbially treated for protection",
    ],
  },
  "industrial-anti-fatigue-mats/comfort-step-42": {
    productPdf: "/docs/anti-fatigue/comfort-step-42_product-sheet.pdf",
    techSpecs: "/docs/anti-fatigue/comfort-step-42_technical-specs.pdf",
    image: "/products/comfort-step-42/2_warehouse.jpg",
    gallery: [
      { src: "/products/comfort-step-42/2_warehouse.jpg", label: "Warehouse workstation" },
      { src: "/products/comfort-step-42/1_machine.jpg", label: "Production line" },
    ],
    swatches: [
      { name: "Black Pebble", src: "/products/comfort-step-42/swatches/black-pebble.jpg" },
      { name: "Black Ribbed", src: "/products/comfort-step-42/swatches/black-ribbed.jpg" },
      { name: "Grey Pebble", src: "/products/comfort-step-42/swatches/grey-pebble.jpg" },
      { name: "Grey Ribbed", src: "/products/comfort-step-42/swatches/grey-ribbed.jpg" },
      { name: "Yellow-edge Pebble", src: "/products/comfort-step-42/swatches/yellow-edge-pebble.jpg" },
      { name: "Yellow-edge Ribbed", src: "/products/comfort-step-42/swatches/yellow-edge-ribbed.jpg" },
    ],
    swatchCount: 6,
    description: [
      "Comfort Step 42 brings thoughtful material design to workstations where employees spend long hours on their feet. Its resilient 3/8-inch vinyl construction creates a supportive surface that helps ease pressure on joints, reduce fatigue, and encourage lasting comfort throughout the workday.",
      "Made for demanding industrial and production environments, Comfort Step 42 combines a clean, functional profile with dependable resistance to fire and chemicals. The result is a carefully engineered anti-fatigue mat that supports employee well-being while helping facilities maintain safer, more comfortable work areas.",
    ],
    applications: [
      "Surface Aspect with Standard Ribbed or Pebble Emboss",
      "Anti-Fatigue positions",
      "Warehouse or hardware retailers",
    ],
    features: [
      "Manufacturing Process: Closed-cell expanded PVC sponge",
      "Commercial-grade durability",
      "Available in multiple sizes and colors",
    ],
  },
  "elements-recycled-rubber-flooring/elements-100-series": {
    productPdf: "/docs/rubber/elements-100_product-sheet.pdf",
    techSpecs: "/docs/rubber/elements-100_technical-specs.pdf",
    image: "/products/elements-100-series/1_studio.jpg",
    swatches: [
      { name: "Black", src: "/products/elements-100-series/swatches/black.jpg" },
      { name: "Light Grey", src: "/products/elements-100-series/swatches/light-grey.jpg" },
      { name: "Blue Light Grey", src: "/products/elements-100-series/swatches/blue-light-grey.jpg" },
      { name: "Blue", src: "/products/elements-100-series/swatches/blue.jpg" },
      { name: "Green", src: "/products/elements-100-series/swatches/green.jpg" },
      { name: "Red", src: "/products/elements-100-series/swatches/red.jpg" },
      { name: "Terra Cotta", src: "/products/elements-100-series/swatches/terra-cotta.jpg" },
      { name: "Tan Clay", src: "/products/elements-100-series/swatches/tan-clay.jpg" },
      { name: "Purple Rain", src: "/products/elements-100-series/swatches/purple-rain.jpg" },
      { name: "Yellow Moon", src: "/products/elements-100-series/swatches/yellow-moon.jpg" },
    ],
    gallery: [
      { src: "/products/elements-100-series/1_studio.jpg", label: "Cycling studio" },
      { src: "/products/elements-100-series/2_cardio.jpg", label: "Cardio & treadmill zone" },
      { src: "/products/elements-100-series/3_fitness-floor.jpg", label: "Fitness center floor" },
      { src: "/products/elements-100-series/4_equipment.jpg", label: "Cardio equipment area" },
      { src: "/products/elements-100-series/5_strength.jpg", label: "Strength & functional training" },
    ],
    description: [
      "Elements 100 Series reflects a careful balance of precision craftsmanship and enduring performance. Each roll is machine-cut to exacting standards, creating a clean, consistent finish that supports accurate placement and a smooth, efficient installation.",
      "Designed for demanding spaces and trusted in applications throughout the United States, Elements 100 Series stands up to heavy use without requiring constant maintenance or premature replacement. Its refined construction and lasting resilience give specifiers confidence in a flooring solution made to perform beautifully, year after year.",
    ],
    applications: [
      "Versatile and customizable recycled rubber flooring",
      "Superior slip resistance in wet or dry conditions",
      "Provides shock absorption and noise reduction",
    ],
    features: [
      "Premium Rubber Backing provides superior grip and dimensional stability",
      "Customization: Available in Sheet/Rolls, and Tiles",
      "Indoor Application use only",
    ],
  },
  "accessories/lock-p799-i-o-rubber": {
    image: "/products/accessories/lock-p799-i-o-rubber.png",
    hideSwatches: true,
    gallery: [
      {
        src: "/products/accessories/lock-p799-i-o-rubber.png",
        label: "Bostik Lock P799 I/O Rubber",
        fit: "contain",
      },
    ],
    description: [
      "A high-performance, moisture-cured polyurethane adhesive formulated specifically for recycled rubber flooring. Its carefully engineered composition creates exceptional bond strength, supporting secure, dependable installations designed to endure the demands of commercial environments.",
    ],
    applications: [
      "Recycled rubber flooring installations",
      "High-performance indoor applications",
      "Areas requiring strong, durable bonding",
    ],
    features: [
      "Moisture-cured polyurethane formula",
      "Exceptional bond strength for rubber materials",
      "Durable and long-lasting performance",
      "Suitable for demanding installation environments",
    ],
  },
  "accessories/lvt-1-8-0": {
    image: "/products/accessories/lvt-1-8-0.svg",
    hideSwatches: true,
    thumbFit: "contain",
    gallery: [
      {
        src: "/products/accessories/lvt-1-8-0.svg",
        label: "LVT 1-8-0 aluminum transition",
        fit: "contain",
        box: "wide",
      },
    ],
    description: [
      "A precisely crafted, low-profile aluminum frame designed to create a clean, refined transition for LVT flooring applications. Pre-drilled with additional fastening points, it provides greater stability and secure placement while maintaining a minimal, unobtrusive profile.",
    ],
    applications: [
      "Designed for LVT installations",
      "Minimal transition height",
      "Increased fastening points for stability",
    ],
    features: [
      'Dimensions: 1.5" W x 8\' L x 0.125" H',
      "Material: Aluminum, mill finish",
      "Drilling: Pre-drilled, 16 holes",
      "Profile: Low profile",
    ],
  },
  "accessories/h-section": {
    image: "/products/accessories/h-section.png",
    hideSwatches: true,
    thumbFit: "contain",
    gallery: [
      {
        src: "/products/accessories/h-section.png",
        label: "H-Section aluminum divider",
        fit: "contain",
      },
    ],
    description: [
      "A thoughtfully engineered aluminum divider with a flexible PVC insert, designed to create a clean, uninterrupted transition between adjoining mat sections. Ideal for front-to-back installations, its discreet profile remains virtually invisible while keeping each section precisely aligned.",
    ],
    applications: [
      "Acts as an invisible divider between adjoining mat sections",
      "Creates a clean continuous transition",
      "Maintains alignment",
    ],
    features: [
      'Dimensions: 0.5" W x 9\' 10" L x 0.4375" H',
      "Material: Aluminum with matching PVC insert",
      "Compatibility: 14mm / 10.5mm matting",
    ],
  },
  "accessories/440-gldstk": {
    image: "/products/accessories/440-gldstk.jpg",
    hideSwatches: true,
    thumbFit: "contain",
    gallery: [
      {
        src: "/products/accessories/440-gldstk.jpg",
        label: "440-GLDSTK multi-purpose nontoxic indoor adhesive",
        fit: "contain",
      },
    ],
    description: [
      "A thoughtfully formulated, non-toxic adhesive designed for versatile indoor flooring and surface applications. Its multi-purpose composition provides a dependable bond for sheet vinyl, VCT, and smooth-backed sheet or roll carpeting, supporting clean, precise installations across a wide range of commercial interiors.",
    ],
    applications: [
      "Multi-purpose formulation for versatile use",
      "Non-toxic and low-odor for indoor environments",
      "Provides reliable adhesion across common flooring materials",
    ],
    features: [
      "Application: Trowel-applied",
      "Use: Indoor Only",
      "Materials: Sheet Vinyl, VCT, Roll Goods Carpeting",
    ],
  },
  "accessories/ramp-5207": {
    image: "/products/accessories/ramp-5207.svg",
    hideSwatches: true,
    thumbFit: "contain",
    gallery: [
      {
        src: "/products/accessories/ramp-5207.svg",
        label: "RAMP-5207 aluminum ramp transition profile",
        fit: "contain",
        box: "wide",
      },
    ],
    description: [
      "A precisely crafted, mill-finished aluminum ramp frame designed to create a smooth, considered transition between surfaces of varying heights. Its pre-drilled construction supports secure, accurate installation, while the gently sloped profile helps reduce trip hazards and maintain comfortable passage throughout the space.",
    ],
    applications: [
      "Smooth transition between surface heights",
      "Reduces trip hazards",
      "Faster, consistent installation",
    ],
    features: [
      'Dimensions: 2" W x 8\' L x 0.312" H',
      "Material: Aluminum, mill finish",
      "Drilling: Pre-drilled, 10 holes",
      "Profile: Ramp profile",
    ],
  },
  "accessories/overlap-ramp-81611": {
    image: "/products/accessories/overlap-ramp-81611.svg",
    hideSwatches: true,
    thumbFit: "contain",
    gallery: [
      {
        src: "/products/accessories/overlap-ramp-81611.svg",
        label: "Overlap Ramp-81611 aluminum overlap ramp profile",
        fit: "contain",
        box: "wide",
      },
    ],
    description: [
      "A precisely crafted, mill-finished aluminum overlap ramp designed to create a secure, refined transition between adjoining surfaces. Its overlapping profile bridges uneven edges with greater stability, while the pre-drilled construction supports accurate installation and dependable long-term placement.",
    ],
    applications: [
      "Creates a secure overlap between adjoining surfaces",
      "Helps bridge uneven edges",
      "Faster, consistent installation",
    ],
    features: [
      'Dimensions: 2.75" W x 8\' L x 0.457" H',
      "Material: Aluminum, mill finish",
      "Drilling: Pre-drilled, 10 holes",
      "Profile: Overlap ramp profile",
    ],
  },
  "accessories/t-section": {
    image: "/products/accessories/t-section.svg",
    hideSwatches: true,
    thumbFit: "contain",
    gallery: [
      {
        src: "/products/accessories/t-section.svg",
        label: "T-Section aluminum divider profile",
        fit: "contain",
      },
    ],
    description: [
      "A precisely crafted, mill-finished aluminum divider designed to bring structure and continuity to expansive left-to-right matting installations. Its clean, understated profile helps maintain accurate alignment across wider spans, creating a stable and visually cohesive finished surface.",
    ],
    applications: [
      "Divides matting sections across wide layouts",
      "Maintains alignment over larger spans",
      "Provides structural separation",
    ],
    features: [
      'Dimensions: 1.0625" W x 9\' 10" L x 0.625" H',
      "Material: Aluminum, mill finish",
      "Drilling: Pre-drilled",
      "Compatibility: 14mm matting",
    ],
  },
  "accessories/edge-r": {
    image: "/products/accessories/edge-r.svg",
    hideSwatches: true,
    thumbFit: "contain",
    gallery: [
      {
        src: "/products/accessories/edge-r.svg",
        label: "Edge R aluminum edge cap profile",
        fit: "contain",
      },
    ],
    description: [
      "A precisely crafted, mill-finished aluminum edge cap designed to secure and protect exposed matting ends. Its clean, understated profile creates a refined transition at the perimeter, giving the installation a stable and thoughtfully finished appearance.",
    ],
    applications: [
      "Caps and secures exposed matting edges",
      "Prevents movement at system boundaries",
      "Creates a clean finished edge",
    ],
    features: [
      'Dimensions: 0.75" W x 9\' 10" L x 0.5625" H',
      "Material: Aluminum, mill finish",
      "Drilling: Pre-drilled",
      "Compatibility: 14mm matting",
    ],
  },
  "broadloom-sheet-roll-goods/fenway-nop": {
    productPdf: "/docs/broadlooms/fenway-nop_product-sheet.pdf",
    techSpecs: "/docs/broadlooms/fenway-nop_technical-specs.pdf",
    image: "/products/fenway/1_school-entrance.jpg",
    swatches: [
      { name: "Charcoal", src: "/products/fenway/swatches/charcoal.jpg" },
      { name: "Sterling", src: "/products/fenway/swatches/sterling.jpg" },
      { name: "Steel Blue", src: "/products/fenway/swatches/steel-blue.jpg" },
      { name: "Sisal", src: "/products/fenway/swatches/sisal.jpg" },
      { name: "Walnut", src: "/products/fenway/swatches/walnut.jpg" },
      { name: "Zinc", src: "/products/fenway/swatches/zinc.jpg" },
    ],
    gallery: [
      { src: "/products/fenway/1_school-entrance.jpg", label: "School entrance mat" },
      { src: "/products/fenway/2_lobby-mat.jpg", label: "Lobby corridor mat" },
      { src: "/products/fenway/3_hotel-corridor.jpg", label: "Hotel corridor runner" },
    ],
    description: [
      "Fenway Nop brings rich texture and lasting character to commercial entrances that must perform through constant use. Its dense, 37-ounce Berber construction is crafted to hold its form beneath heavy foot traffic while remaining lightweight enough for efficient installation and routine care.",
      "Made from 100% solution-dyed, UV-stabilized ASOTA® polypropylene, Fenway Nop maintains its appearance through changing conditions. The material can be precisely cut into custom shapes without fraying, making it a versatile foundation for both understated entrance mats and carefully detailed logo designs.",
    ],
    applications: [
      "UV stable dense 37oz. construction",
      "Can be cut into any shape without edge ravel",
      "Great choice for logo and plain mats",
    ],
    features: [
      "100% Solution-dyed UV stabilized ASOTA® polypropylene",
      "37 oz. Berber Needle-punched Contract Carpet",
    ],
  },
  "broadloom-sheet-roll-goods/atlas": {
    productPdf: "/docs/broadlooms/atlas_product-sheet.pdf",
    techSpecs: "/docs/broadlooms/atlas_technical-specs.pdf",
    image: "/products/atlas/g0_entrance-door.jpg",
    swatches: [
      { name: "Black Shadow", src: "/products/atlas/swatches/black-shadow.jpg" },
      { name: "Charcoal", src: "/products/atlas/swatches/charcoal.jpg" },
      { name: "Sterling", src: "/products/atlas/swatches/sterling.jpg" },
    ],
    gallery: [
      { src: "/products/atlas/g0_entrance-door.jpg", label: "Entrance doorway" },
      { src: "/products/atlas/g1_entrance.jpg", label: "Building entrance" },
      { src: "/products/atlas/g2_conference.jpg", label: "Conference room" },
    ],
    description: [
      "Atlas brings tailored texture and enduring material strength to entrances shaped by heavy traffic and changing weather. Its dense 44-ounce construction creates a resilient, comfortable surface, while the classic corduroy rib pattern captures dirt, moisture, and debris before they can travel farther into the building.",
      "Crafted from solution-dyed, UV-stabilized ASOTA® polypropylene fibers, Atlas resists crushing and staining while retaining its character through demanding use and extreme climate conditions. The material can also be precisely cut to suit the space without edge ravel, giving architects and facility teams a versatile entrance solution that balances refined structure with lasting protection.",
    ],
    applications: [
      "Resilient in extreme climate conditions",
      "Easily cut into any shape without edge ravel",
    ],
    features: [
      "Dense 44 oz. material provides long-term comfort",
      "Provides an unparalleled premium look and feel",
      "Available in three colors: Black Shadow, Charcoal & Sterling",
    ],
  },
  "broadloom-sheet-roll-goods/cougar": {
    productPdf: "/docs/broadlooms/cougar_product-sheet.pdf",
    techSpecs: "/docs/broadlooms/cougar_technical-specs.pdf",
    image: "/products/cougar/2_entrance.jpg",
    swatches: [
      { name: "Charcoal", src: "/products/cougar/swatches/charcoal.jpg" },
    ],
    gallery: [
      { src: "/products/cougar/1_elevator-lobby.jpg", label: "Elevator lobby entrance" },
      { src: "/products/cougar/2_entrance.jpg", label: "Building entrance mat" },
      { src: "/products/cougar/3_pharmacy.jpg", label: "Retail pharmacy entry" },
    ],
    description: [
      "Cougar brings substantial texture and enduring character to commercial entrances shaped by medium-to-heavy foot traffic. Its distinctive double-rib pattern is formed from rich, densely constructed all-weather fibers, creating a tailored surface that helps retain its appearance under demanding daily use.",
      "Crafted with a heavy 44-ounce construction, solution-dyed and UV-stabilized ASOTA® polypropylene fibers, and a premium composite rubber backing, Cougar is designed to perform reliably across changing climates. Easy to handle, install, and cut into custom shapes without edge ravel, it balances precise craftsmanship with dependable entrance protection.",
    ],
    applications: [
      "High-performance double-ribbed construction",
      "Solution-dyed, UV stabilized ASOTA® polypropylene fiber",
      "Available in sheet/roll goods",
    ],
    features: [
      "Rich and dense all-weather fibers",
      "Heavy 44 oz. construction with premium composite rubber backing",
    ],
  },
  "broadloom-sheet-roll-goods/natural-coco-coir": {
    productPdf: "/docs/broadlooms/natural-coco_product-sheet.pdf",
    techSpecs: "/docs/broadlooms/natural-coco_technical-specs.pdf",
    image: "/products/natural-coco/1_entry.jpg",
    swatches: [
      { name: "Natural", src: "/products/natural-coco/swatches/natural.jpg" },
      { name: "Grey", src: "/products/natural-coco/swatches/grey.jpg" },
      { name: "Black", src: "/products/natural-coco/swatches/black.jpg" },
      { name: "Blue", src: "/products/natural-coco/swatches/blue.jpg" },
      { name: "Green", src: "/products/natural-coco/swatches/green.jpg" },
      { name: "Brown", src: "/products/natural-coco/swatches/brown.jpg" },
      { name: "Red", src: "/products/natural-coco/swatches/red.jpg" },
    ],
    gallery: [
      { src: "/products/natural-coco/1_entry.jpg", label: "Recessed well entryway" },
      { src: "/products/natural-coco/2_black-rollgood.jpg", label: "Black coir roll goods" },
      { src: "/products/natural-coco/3_detail.jpg", label: "Natural coir fiber detail" },
    ],
    description: [
      "Made with natural coconut/coir fibers, this venerable matting provides an unparalleled premium, natural look and feel for indoor applications. This unique, natural mat is ideal for recessed wells and entryways.",
      "Perfect for projecting an environmental or health-conscious image, Natural Coco/Coir can be created with or without a logo. Its dense, fusion-bonded surface suits office environments, lobbies, libraries, conference rooms, retail stores, and runners.",
    ],
    applications: [
      "Natural coconut/coir fibers for an authentic, premium look and feel",
      "Ideal for recessed wells and entryways (indoor only)",
      "Can be created with or without a custom logo",
    ],
    features: [
      "60 oz/sy pile weight, fusion-bonded non-directional smooth/velour construction",
      "Available in seven colors: Natural, Grey, Black, Blue, Green, Brown & Red",
    ],
  },
  "broadloom-sheet-roll-goods/synthetic-coco": {
    productPdf: "/docs/broadlooms/synthetic-coco_product-sheet.pdf",
    techSpecs: "/docs/broadlooms/synthetic-coco_technical-specs.pdf",
    image: "/products/syncoco/1_library-entrance.jpg",
    swatches: [
      { name: "Natural", src: "/products/syncoco/swatches/natural.jpg" },
    ],
    gallery: [
      { src: "/products/syncoco/1_library-entrance.jpg", label: "Library entrance" },
      { src: "/products/syncoco/2_entry-overhead.jpg", label: "Recessed mat well entry" },
    ],
    description: [
      "Synthetic Coco captures the warm, organic character of natural coir in a material crafted for greater consistency and endurance. Its dense surface is made from solution-dyed, UV-stabilized polypropylene fibers, providing the familiar appearance of coco matting without the shedding and maintenance concerns associated with natural fibers.",
      "Designed for recessed mat wells and wall-to-wall installations, its substantial face weight withstands demanding foot traffic while its absorbent texture helps collect moisture at the entrance. The result is a thoughtfully constructed indoor-outdoor flooring solution that pairs natural beauty with dependable, long-term performance.",
    ],
    applications: [
      "100% solution-dyed UV stabilized polypropylene fibers are stained and fade resistant",
      "Heavy, dense face weight",
      "Ideal for long-term usage in even the most heavily trafficked areas",
    ],
    features: [
      "Absorbent surface assists in soaking up water, keeping surrounding floor surfaces dry and safe from slips",
      "Best suited for recessed mat wells or wall-to-wall installations",
    ],
  },
  "broadloom-sheet-roll-goods/windsor": {
    productPdf: "/docs/broadlooms/windsor_product-sheet.pdf",
    techSpecs: "/docs/broadlooms/windsor_technical-specs.pdf",
    image: "/products/windsor/1_boutique.jpg",
    swatches: [
      { name: "Charcoal", src: "/products/windsor/swatches/charcoal-hd.jpg" },
      { name: "Dark Grey", src: "/products/windsor/swatches/dark-grey-hd.jpg" },
      { name: "Taupe", src: "/products/windsor/swatches/taupe-hd.jpg" },
      { name: "Navy", src: "/products/windsor/swatches/navy.jpg" },
      { name: "Red", src: "/products/windsor/swatches/red.jpg" },
    ],
    gallery: [
      { src: "/products/windsor/1_boutique.jpg", label: "Retail boutique" },
      { src: "/products/windsor/2_spa-corridor.jpg", label: "Spa corridor" },
      { src: "/products/windsor/3_lounge.jpg", label: "Amenity lounge" },
    ],
    description: [
      "Windsor Rolls draws its distinctive character from the layered striations found throughout the natural world. Subtle shifts in color and texture create warmth, movement, and visual depth, giving commercial interiors a refined foundation that feels carefully composed rather than purely functional.",
      "Crafted from 100% PA6 space-dyed nylon with a durable 30-ounce tufted construction, Windsor is designed to retain its beauty in demanding environments. Available in five colors, as well as coordinating tile and custom logo formats, it provides designers with the freedom to build cohesive spaces around a shared material expression.",
    ],
    applications: [
      "Beautiful, striated design available in five colors",
      "Tufted 30 oz nylon provides superior performance",
      "Available in carpet tile, sheet/roll goods & custom/logo matting",
    ],
    features: [
      "30 oz. Designer Striae Nylon Carpet Rolls",
      "100% PA6 Space Dyed Nylon",
    ],
  },
  "custom-logos/two-n-one-mat": {
    productPdf: "/docs/custom-logos/two-n-one_product-sheet.pdf",
    techSpecs: "/docs/custom-logos/two-n-one_technical-specs.pdf",
    image: "/products/two-n-one/1_logo-mat.jpg",
    hideSwatches: true,
    gallery: [
      { src: "/products/two-n-one/1_logo-mat.jpg", label: "Custom logo entrance" },
      { src: "/products/two-n-one/2_store-entrance.jpg", label: "Retail storefront entry" },
      { src: "/products/two-n-one/3_product.jpg", label: "Grid + carpet construction", fit: "contain" },
    ],
    description: [
      "Two-N-One Mat brings two complementary materials together in a carefully integrated entrance system. A serrated grid surface first scrapes and captures coarse dirt and debris, while the adjoining all-weather carpet removes remaining soil and excess moisture before it can travel farther into the building.",
      "Engineered for medium- to high-traffic entrances, its streamlined construction supports the comfortable movement of heels, wheels, and carts. Fully customizable for semi-permanent, wall-to-wall installations, Two-N-One Mat transforms practical floor protection into a cohesive architectural feature shaped specifically for the space.",
    ],
    applications: [
      "Dual-action cleaning",
      "Built to last with industry-leading assembly process",
      "Fully customizable for semi-permanent wall-to-wall installation",
    ],
    features: [
      "Grid Function: Serrated grid scrapes & traps dirt and debris",
      "Carpet Function: Walk-off carpet removes remaining dirt & excess moisture",
      "Traffic Rating: Medium to high traffic environments",
    ],
  },
  "elements-recycled-rubber-flooring/elements-200-series": {
    productPdf: "/docs/rubber/elements-200_product-sheet.pdf",
    techSpecs: "/docs/rubber/elements-200_technical-specs.pdf",
    image: "/products/elements-200-series/3_cardio.jpg",
    swatches: [
      { name: "Blue Pool", src: "/products/elements-200-series/swatches/blue-pool.jpg" },
      { name: "Blue", src: "/products/elements-200-series/swatches/blue.jpg" },
      { name: "Canned Heat", src: "/products/elements-200-series/swatches/canned-heat.jpg" },
      { name: "Crushed Nuts", src: "/products/elements-200-series/swatches/crushed-nuts.jpg" },
      { name: "Gold Rush", src: "/products/elements-200-series/swatches/gold-rush.jpg" },
      { name: "Orange", src: "/products/elements-200-series/swatches/orange.jpg" },
      { name: "Snow Flakes", src: "/products/elements-200-series/swatches/snow-flakes.jpg" },
      { name: "Titan Grey", src: "/products/elements-200-series/swatches/titan-grey.jpg" },
    ],
    gallery: [
      { src: "/products/elements-200-series/ice-rink-v2.jpg", label: "Ice rink" },
      { src: "/products/elements-200-series/canned-heat.jpg", label: "Canned Heat" },
      { src: "/products/elements-200-series/1_training-center.jpg", label: "Cardio studio" },
    ],
    description: [
      "Elements 200 Series brings strength, material integrity, and lasting character to demanding fitness and training environments. Its dense rubber composition provides dependable shock absorption and firm support underfoot, helping protect equipment, athletes, and the flooring beneath them through years of intensive use.",
      "Crafted from high-quality recycled rubber and accented with EPDM color granules, Elements 200 Series contains 30–100% post-consumer recycled content. The result is a thoughtfully engineered flooring surface that balances exceptional durability, distinctive visual texture, and a more responsible approach to material use.",
    ],
    applications: [
      "The highest quality rubber gym flooring available",
      "May qualify for LEED credits as part of a green building project",
      "Our EPDM color granules are made from 30-100% post-consumer",
    ],
    features: [
      "SBR Rubber Black + 20% Color EPDM/Recycled Rubber",
      "Available in Rolls, Square Tiles and Interlocking Tiles",
    ],
  },
  "elements-recycled-rubber-flooring/elements-350-series": {
    productPdf: "/docs/rubber/elements-350_product-sheet.pdf",
    techSpecs: "/docs/rubber/elements-350_technical-specs.pdf",
    image: "/products/elements-350-series/1_gym.jpg",
    swatches: [
      { name: "Blue Bahia", src: "/products/elements-350-series/swatches/blue-bahia.jpg" },
      { name: "Fiesta Purple", src: "/products/elements-350-series/swatches/fiesta-purple.jpg" },
      { name: "Grass Green", src: "/products/elements-350-series/swatches/grass-green.jpg" },
      { name: "Milky Way", src: "/products/elements-350-series/swatches/milky-way.jpg" },
      { name: "Rouge", src: "/products/elements-350-series/swatches/rouge.jpg" },
      { name: "Salt-n-Pepper", src: "/products/elements-350-series/swatches/salt-n-pepper.jpg" },
      { name: "Terrific Tan", src: "/products/elements-350-series/swatches/terrific-tan.jpg" },
    ],
    gallery: [
      { src: "/products/elements-350-series/1_gym.jpg", label: "Strength training floor" },
      { src: "/products/elements-350-series/2_free-weights.jpg", label: "Free-weight area" },
      { src: "/products/elements-350-series/3_training-floor.jpg", label: "Performance training floor" },
    ],
    description: [
      "Elements 350 Series pairs the strength and resilience of high-performance rubber flooring with a more expressive visual character. Vibrant EPDM color granules animate the surface, bringing energy and dimension to fitness and training spaces while dependable shock absorption supports demanding activity.",
      "Crafted with 30\u2013100% post-consumer recycled content, Elements 350 Series is designed for long-term performance and straightforward maintenance. Its considered material composition can also support LEED-focused building projects, balancing bold design, lasting durability, and responsible construction.",
    ],
    applications: [
      "The highest quality rubber gym flooring available",
      "May qualify for LEED credits as part of a green building project",
      "Our EPDM color granules are made from 30-100% post-consumer",
      "Easy Maintenance",
    ],
    features: [
      "SBR Rubber Black + 35% Color EPDM/Recycled Rubber",
      "Available in Rolls, Square Tiles and Interlocking Tiles",
    ],
  },
  "elements-recycled-rubber-flooring/elements-950-series": {
    productPdf: "/docs/rubber/elements-950_product-sheet.pdf",
    techSpecs: "/docs/rubber/elements-950_technical-specs.pdf",
    image: "/products/elements-950-series/1_track.jpg",
    swatchNote: "Note: The 950 Series has two colors that are STOCKED, all other colors listed are custom and have minimums, plus longer lead times.",
    swatches: [
      { name: "Granite Grey (Stocked)", src: "/products/elements-950-series/swatches/granite-grey.jpg" },
      { name: "Grizzly (Stocked)", src: "/products/elements-950-series/swatches/grizzly.jpg" },
      { name: "Gulf Blue", src: "/products/elements-950-series/swatches/gulf-blue.jpg" },
      { name: "Metal In Mud", src: "/products/elements-950-series/swatches/metal-in-mud.jpg" },
      { name: "Shooting Star", src: "/products/elements-950-series/swatches/shooting-star.jpg" },
      { name: "Tempe Sunset", src: "/products/elements-950-series/swatches/tempe-sunset.jpg" },
      { name: "Volcanic Ash", src: "/products/elements-950-series/swatches/volcanic-ash.jpg" },
      { name: "Volcanic Flow", src: "/products/elements-950-series/swatches/volcanic-flow.jpg" },
    ],
    gallery: [
      { src: "/products/elements-950-series/1_track.jpg", label: "Strength & conditioning floor" },
      { src: "/products/elements-950-series/2_red-floor.jpg", label: "Performance training area" },
      { src: "/products/elements-950-series/3_kettlebell.jpg", label: "Functional fitness zone" },
    ],
    description: [
      "Elements 950 Series brings substantial strength and distinctive material character to demanding fitness environments. Its dense rubber construction supports heavy equipment while absorbing sound and impact, helping create a quieter, more composed training space.",
      "Crafted from 100% recycled rubber tires and accented with post-industrial EPDM color chips, Elements 950 Series transforms reclaimed materials into a resilient, easy-to-maintain surface. Moisture resistance and antimicrobial performance support a cleaner environment, balancing enduring durability with responsible design.",
    ],
    applications: [
      "The highest quality rubber gym flooring available",
      "Easy Maintenance",
    ],
    features: [
      "SBR Rubber Black + 95% Color EPDM/Recycled Rubber",
      "Available in Rolls, Square Tiles and Interlocking Tiles",
    ],
  },
  "elements-recycled-rubber-flooring/monster-tile-100-350-950-series": {
    productPdf: "/docs/rubber/monster-tile_product-sheet.pdf",
    techSpecs: "/docs/rubber/monster-tile_technical-specs.pdf",
    image: "/products/monster-tile/1_gym.jpg",
    gallery: [
      { src: "/products/monster-tile/1_gym.jpg", label: "Fitness facility floor" },
      { src: "/products/monster-tile/2_weightroom.jpg", label: "Weight training area" },
      { src: "/products/monster-tile/3_tile-detail.jpg", label: "Tile construction detail", fit: "contain" },
    ],
    swatches: [
      { name: "Avalanche", src: "/products/monster-tile/swatches/avalanche.jpg" },
      { name: "Granite Grey", src: "/products/monster-tile/swatches/granite-grey.jpg" },
      { name: "Graphite", src: "/products/monster-tile/swatches/graphite.jpg" },
      { name: "Storm Cloud", src: "/products/monster-tile/swatches/storm-cloud.jpg" },
      { name: "Mica", src: "/products/monster-tile/swatches/mica.jpg" },
      { name: "Sandstone", src: "/products/monster-tile/swatches/sandstone.jpg" },
      { name: "Hot Chocolate", src: "/products/monster-tile/swatches/hot-chocolate.jpg" },
    ],
    description: [
      "Monster Rubber Tiles bring substantial strength and purposeful material design to the most demanding training environments. Their ultra-thick, dense rubber construction absorbs the impact of dropped weights while helping reduce noise and vibration throughout the facility, protecting both the subfloor and surrounding space.",
      "The modular tile format creates a solid, cohesive surface while supporting straightforward installation, maintenance, and replacement. Built for intensive weightlifting and high-impact exercise areas, Monster Rubber Tiles deliver lasting resilience, powerful shock absorption, and the confidence of flooring crafted to perform under pressure.",
    ],
    applications: [
      "A classic combination of performance & design",
      "Standard Square Tile Dimensions is 24” x 24” x 1”",
    ],
    features: [
      "Heavy Duty Commercial Rubber Flooring",
      "100% Recycled SBR Rubber + % selected of EPDM granules & polyurethane binder",
    ],
  },
  "carpet-tile-goods/champion-xp": {
    productPdf: "/docs/carpet-tile/champion-xp_product-sheet.pdf",
    techSpecs: "/docs/carpet-tile/champion-xp_technical-specs.pdf",
    image: "/products/champion-xp/library-thumb.jpg",
    swatches: [
      { name: "Anthracite", src: "/products/champion-xp/swatches/anthracite.jpg" },
      { name: "Aspen", src: "/products/champion-xp/swatches/aspen.jpg" },
      { name: "Azure", src: "/products/champion-xp/swatches/azure.jpg" },
      { name: "Beige", src: "/products/champion-xp/swatches/beige.jpg" },
      { name: "Black Shadow", src: "/products/champion-xp/swatches/black-shadow.jpg" },
      { name: "Bordeaux", src: "/products/champion-xp/swatches/bordeaux.jpg" },
      { name: "Chocolate", src: "/products/champion-xp/swatches/chocolate.jpg" },
      { name: "Harvest", src: "/products/champion-xp/swatches/harvest.jpg" },
      { name: "Midnight Blue", src: "/products/champion-xp/swatches/midnight-blue.jpg" },
      { name: "Sisal", src: "/products/champion-xp/swatches/sisal.jpg" },
      { name: "Sterling", src: "/products/champion-xp/swatches/sterling.jpg" },
    ],
    gallery: [
      { src: "/products/champion-xp/1_grocery.jpg", label: "Grocery entrance" },
      { src: "/products/champion-xp/2_retail.jpg", label: "Retail storefront" },
      { src: "/products/champion-xp/3_lounge.jpg", label: "Amenity lounge" },
    ],
    description: [
      "Champion XP Tile brings rich Berber texture and dependable performance to busy commercial entrances. Its durable fiber-bonded surface is thoughtfully constructed to capture grit, moisture, and everyday debris before they can travel farther into the building, helping preserve the beauty and condition of surrounding floors.",
      "Designed with a high-heel-friendly profile, Champion XP Tile supports comfortable passage while maintaining a clean, tailored appearance. Its modular format, strong dirt-trapping capability, and practical value make it a versatile entrance solution that balances textural character, easier maintenance, and lasting floor protection.",
    ],
    applications: [
      "A particularly effective and stylish entrance flooring",
      "Perfectly at home in high-traffic environments",
      "Provides extra-high dirt absorption",
    ],
    features: [
      "Available in Sheet/Rolls, Carpet Tiles and Custom logo mats.",
      "Made with 100% ASOTA® polypropylene fibers",
      "Durable and a highly resilient non-woven carpet",
    ],
  },
  "carpet-tile-goods/dominator": {
    productPdf: "/docs/carpet-tile/dominator_product-sheet.pdf",
    techSpecs: "/docs/carpet-tile/dominator_technical-specs.pdf",
    image: "/products/dominator/1_fitness.jpg",
    swatches: [
      { name: "Anthracite", src: "/products/dominator/swatches/anthracite.jpg" },
      { name: "Mid Grey", src: "/products/dominator/swatches/mid-grey.jpg" },
      { name: "Pebble", src: "/products/dominator/swatches/pebble.jpg" },
      { name: "Indigo Blue", src: "/products/dominator/swatches/indigo-blue.jpg" },
      { name: "Heath", src: "/products/dominator/swatches/heath.jpg" },
      { name: "Walnut", src: "/products/dominator/swatches/walnut.jpg" },
    ],
    gallery: [
      { src: "/products/dominator/1_fitness.jpg", label: "Fitness center" },
      { src: "/products/dominator/2_vestibule.jpg", label: "Retail vestibule" },
      { src: "/products/dominator/3_grocery-entrance.jpg", label: "Grocery entrance" },
    ],
    description: [
      "Dominator Carpet Tiles bring substantial texture and enduring strength to commercial spaces shaped by constant movement. Crafted from 100% ASOTA® polypropylene fibers, their dense nonwoven surface creates a resilient foundation that retains its character beneath heavy foot traffic, rolling carts, and the demands of everyday use.",
      "UV stable and stain resistant, Dominator is thoughtfully engineered for long-term performance in hardworking entryways, retail settings, and workplaces. Its modular construction combines practical flexibility with a clean, professional appearance, creating an inviting floor designed to perform beautifully in the busiest areas.",
    ],
    applications: [
      "Our heaviest and most durable product in the line",
      "Inviting, resilient design made for long-term wear",
      "Available with Bitumen & TacFast backing",
    ],
    features: [
      "Face Weight: 60 oz. Nonwoven Contract Carpet Tile",
      "Construction: Nonwoven Diagonal Pattern Fiber",
      "Fiber Content: 100% Solution-dyed UV stabilized ASOTA® polypropylene",
    ],
  },
  "carpet-tile-goods/diplomat": {
    productPdf: "/docs/carpet-tile/diplomat_product-sheet.pdf",
    techSpecs: "/docs/carpet-tile/diplomat_technical-specs.pdf",
    image: "/products/diplomat/vestibule-wide-v2.jpg",
    swatches: [
      { name: "Carbon", src: "/products/diplomat/swatches/carbon.jpg" },
      { name: "Smoke", src: "/products/diplomat/swatches/smoke.jpg" },
      { name: "Mink", src: "/products/diplomat/swatches/mink.jpg" },
      { name: "Heath", src: "/products/diplomat/swatches/heath.jpg" },
      { name: "Tan", src: "/products/diplomat/swatches/tan.jpg" },
    ],
    gallery: [
      { src: "/products/diplomat/1_vestibule.jpg", label: "Grocery vestibule" },
      { src: "/products/diplomat/2_grocery.jpg", label: "Grocery entrance" },
      { src: "/products/diplomat/3_dental-office.jpg", label: "Dental office reception" },
    ],
    description: [
      "Diplomat Carpet Tiles bring understated texture and dependable resilience to commercial spaces shaped by constant activity. Crafted from 100% ASOTA® polypropylene fibers, their durable surface is designed to retain its character beneath heavy foot traffic, rolling carts, and the everyday demands of busy entryways and retail environments.",
      "The modular construction supports efficient installation, care, and selective replacement while maintaining a clean, cohesive appearance across the floor. Thoughtfully made for long-term use, Diplomat balances practical strength with refined simplicity, creating a professional foundation that continues to perform in the spaces that work hardest.",
    ],
    applications: [
      "Made with 100% ASOTA® polypropylene fibers",
      "UV stable, stain resistant",
      "Durable and a highly resilient non-woven carpet",
    ],
    features: [
      "Construction: Nonwoven high/low dirt pocket pattern",
      "Fiber Content: 100% Solution-dyed UV stabilized ASOTA® polypropylene",
      "Face Weight: 60 oz. High-Low Structured Loop Carpet Tile",
    ],
  },
  "carpet-tile-goods/atlas-plus": {
    productPdf: "/docs/carpet-tile/atlas-plus_product-sheet.pdf",
    techSpecs: "/docs/carpet-tile/atlas-plus_technical-specs.pdf",
    image: "/products/atlas-plus/1_library.jpg",
    swatches: [
      { name: "Charcoal", src: "/products/atlas-plus/swatches/charcoal.jpg" },
    ],
    gallery: [
      { src: "/products/atlas-plus/4_charcoal-detail.jpg", label: "Charcoal tile detail" },
      { src: "/products/atlas-plus/1_library.jpg", label: "Library entrance" },
      { src: "/products/atlas-plus/2_office-entry.jpg", label: "Office entry" },
      { src: "/products/atlas-plus/3_grocery-welcome.jpg", label: "Grocery welcome mat" },
    ],
    description: [
      "Atlas Plus brings rhythm, texture, and architectural order to active commercial interiors. Its distinctive linear surface draws inspiration from tailored textiles, creating a refined foundation that gives modern spaces a sense of structure while helping disguise tracked-in dirt and the visible effects of daily use.",
      "Crafted with durable needle-punched construction and solution-dyed, UV-stabilized polypropylene fibers, Atlas Plus is designed to retain its character through demanding foot traffic. Its modular format and choice of eco-bitumen or TacFast™ backing offer installation flexibility, bringing together thoughtful design, material resilience, and lasting commercial performance.",
    ],
    applications: [
      "High-performance needle-punched construction",
      "Made to perform using solution-dyed, UV stabilized ASOTA® polypropylene fiber",
      "Highly versatile and available in premium (eco) bitumen or TacFast™ backing",
    ],
    features: [
      "60 oz. Corduroy Rib Needle-punched carpet tile",
    ],
  },
  "entry-grid-systems/snap-trax-plus": {
    productPdf: "/docs/entry-grid/snap-trax-plus_product-sheet.pdf",
    techSpecs: "/docs/entry-grid/snap-trax-plus_technical-specs.pdf",
    image: "/products/snap-trax-plus/1_grocery-entrance.jpg",
    swatches: [
      { name: "Dark Grey", src: "/products/snap-trax-plus/swatches/dark-grey.jpg" },
    ],
    gallery: [
      { src: "/products/snap-trax-plus/1_grocery-entrance.jpg", label: "Grocery entrance" },
      { src: "/products/snap-trax-plus/2_vestibule.jpg", label: "Grocery vestibule" },
    ],
    description: [
      "Snap Trax brings sculpted form and purposeful engineering to entrances shaped by constant movement. Its carefully designed surface combines scraping and drying action to remove dirt, moisture, and debris from footwear before they can travel farther into the building, helping surrounding floors remain cleaner and safer.",
      "Crafted from a recyclable, UV-resistant TPE/PVC vinyl compound, Snap Trax maintains its shape without shrinking, gapping, or shifting over time. Its modular tile construction can be expanded, configured, and tailored to the architecture of nearly any vestibule, creating a cohesive entrance system that balances adaptable design with lasting strength in high-shear, high-load environments.",
    ],
    applications: [
      "Interlocking 50 cm × 50 cm tiles",
      "Will not shrink or gap",
      "Integrated canals that allow dirt and debris to fall through",
    ],
    features: [
      "Type: Modular Grid System with Carpet",
      "Material: TPE/PVC with Nylon Carpet",
    ],
  },
  "entry-grid-systems/frontrunner": {
    productPdf: "/docs/entry-grid/frontrunner_product-sheet.pdf",
    techSpecs: "/docs/entry-grid/frontrunner_technical-specs.pdf",
    image: "/products/frontrunner/1_stair-landing.jpg",
    gallery: [
      { src: "/products/frontrunner/1_stair-landing.jpg", label: "Stair landing runner" },
      { src: "/products/frontrunner/2_retail-entrance.jpg", label: "Retail entrance" },
      { src: "/products/frontrunner/3_profile.jpg", label: "Grid profile — 14mm", fit: "contain" },
    ],
    swatches: [
      { name: "Black", src: "/products/frontrunner/swatches/black.jpg" },
      { name: "Grey", src: "/products/frontrunner/swatches/grey.jpg" },
      { name: "Brown", src: "/products/frontrunner/swatches/brown.jpg" },
    ],
    description: [
      "Frontrunner brings clean architectural structure and thoughtful accessibility to high-traffic entrances. Its closely spaced open-grid surface is carefully designed to provide comfortable passage for high heels, wheelchairs, shopping carts, and other wheeled traffic while allowing tracked-in dirt and debris to fall beneath the walking surface.",
      "Crafted from hard-wearing, flexible PVC and supplied in convenient roll formats, Frontrunner creates a cohesive entrance surface with fewer interruptions. The result is a durable, accessible system that balances purposeful design, easier maintenance, and dependable everyday performance.",
    ],
    applications: [
      "Frontrunner is made from a hard-wearing, flexible PVC",
      "Wheeled traffic friendly including shopping trolleys",
      "Open grid design lets debris fall through",
    ],
    features: [
      "Material: Flexible PVC",
      "Profile: Available in regular & Low-Profile",
    ],
  },
  "entry-grid-systems/frontrunner-plus": {
    productPdf: "/docs/entry-grid/frontrunner-plus_product-sheet.pdf",
    techSpecs: "/docs/entry-grid/frontrunner-plus_technical-specs.pdf",
    image: "/products/frontrunner-plus/1_entrance.jpg",
    gallery: [
      { src: "/products/frontrunner-plus/1_entrance.jpg", label: "Entrance vestibule" },
      { src: "/products/frontrunner-plus/2_retail.jpg", label: "Retail entrance" },
      { src: "/products/frontrunner-plus/3_profile.jpg", label: "Grid profile — 16mm", fit: "contain" },
    ],
    swatches: [
      { name: "Smoke 608 (Black vinyl)", src: "/products/frontrunner-plus/swatches/black-vinyl-smoke-608-carpet.jpg" },
      { name: "Steel Grey 606 (Grey LP vinyl)", src: "/products/frontrunner-plus/swatches/grey-lp-vinyl-steel-grey-606-carpet.jpg" },
      { name: "Coffee Bean 605 (Brown LP vinyl)", src: "/products/frontrunner-plus/swatches/brown-lp-vinyl-coffee-bean-605-carpet.jpg" },
    ],
    description: [
      "Frontrunner Plus brings tailored texture and thoughtful accessibility to high-traffic entrances. Its closely spaced, open-grid surface is carefully designed to support comfortable passage for high heels, wheelchairs, shopping carts, and other wheeled traffic while allowing tracked-in dirt and debris to settle below the walking surface.",
      "Constructed with a Colback nonwoven polyester primary backing and comfort vinyl, Frontrunner Plus combines material resilience with a more refined experience underfoot. The result is an accessible entrance system that balances clean architectural character, practical maintenance, and dependable everyday performance.",
    ],
    applications: [
      "Frontrunner Plus is made from a hard-wearing, flexible PVC",
      "Wheeled traffic friendly including shopping trolleys",
      "Open grid design lets debris fall through",
    ],
    features: [
      "Material: Flexible PVC",
      "Backing: Colback non-woven polyester & comfort vinyl",
    ],
  },
  "industrial-anti-fatigue-mats/comfort-step-diamond": {
    productPdf: "/docs/anti-fatigue/comfort-step-diamond_product-sheet.pdf",
    techSpecs: "/docs/anti-fatigue/comfort-step-diamond_technical-specs.pdf",
    image: "/products/comfort-step-diamond/1_autoshop.jpg",
    gallery: [
      { src: "/products/comfort-step-diamond/1_autoshop.jpg", label: "Machine shop workstation" },
      { src: "/products/comfort-step-diamond/2_warehouse.jpg", label: "Assembly workbench" },
      { src: "/products/comfort-step-diamond/3_mat.jpg", label: "Diamond mat", fit: "contain" },
    ],
    swatches: [
      { name: "Black", src: "/products/comfort-step-diamond/swatches/black.jpg" },
    ],
    description: [
      "Comfort Step Diamond brings thoughtful engineering and layered material design to workspaces where comfort and dependable footing are essential. Its integrated, slip-resistant wear layer of high-density vinyl foam is polymerically bonded to a resilient vinyl sponge cushion, creating a unified surface built for lasting support and performance.",
      "Smooth yet supple underfoot, the durable surface is easy to clean, while the expanded foam foundation helps reduce stress on legs and joints during extended periods of standing. The result is a carefully constructed anti-fatigue solution that balances strength, comfort, and everyday practicality.",
    ],
    applications: [
      "Slip-resistant wear layer of high-density vinyl foam",
      "Diamond pebble surface",
    ],
    features: [
      "1/2” Durable Dual-Layer Anti-Fatigue Mat",
      "Indoor Use Only",
    ],
  },
  "industrial-anti-fatigue-mats/kitchen-mat": {
    productPdf: "/docs/anti-fatigue/kitchen-mat_product-sheet.pdf",
    techSpecs: "/docs/anti-fatigue/kitchen-mat_technical-specs.pdf",
    image: "/products/kitchen-mat/1_hardware.jpg",
    gallery: [
      { src: "/products/kitchen-mat/1_hardware.jpg", label: "Tool rental counter" },
      { src: "/products/kitchen-mat/2_kitchen.jpg", label: "Commercial kitchen" },
      { src: "/products/kitchen-mat/3_mat.jpg", label: "Drainage mat", fit: "contain" },
    ],
    swatches: [
      { name: "Black", src: "/products/kitchen-mat/swatches/black.jpg" },
      { name: "Red", src: "/products/kitchen-mat/swatches/red.jpg" },
      { name: "Black with GrittTuff™", src: "/products/kitchen-mat/swatches/black-with-grittuff.jpg" },
    ],
    description: [
      "Industrial Kitchen Mat is thoughtfully shaped for workspaces where moisture, grease, and constant movement create daily challenges. Its resilient half-inch rubber construction provides supportive cushioning underfoot, while an open pattern of rectangular drainage holes allows liquids to pass through the surface instead of collecting around employees’ feet.",
      "Fully launderable and available in formulations suited to wet or grease-prone areas, the mat brings dependable traction and anti-fatigue comfort to commercial kitchens, bars, machine shops, and industrial workstations. An optional GrittTuff carbide-grit surface provides additional grip where demanding conditions call for greater stability.",
    ],
    applications: [
      "Rectangular Walls with Drainage Holes",
      "Black (for wet areas) & Red (grease/oil resistant)",
      "Grease/Oil Resistant",
    ],
    features: [
      'Thickness: 1/2" Anti-Fatigue matting with Drainage holes',
      "Material: 50% Nitrile Rubber",
    ],
  },
  "industrial-anti-fatigue-mats/diamond-plate-safety-step": {
    productPdf: "/docs/anti-fatigue/diamond-plate-safety-step_product-sheet.pdf",
    techSpecs: "/docs/anti-fatigue/diamond-plate-safety-step_technical-specs.pdf",
    image: "/products/safestep/5_product.jpg",
    gallery: [
      { src: "/products/safestep/5_product.jpg", label: "Machine shop workstation" },
      { src: "/products/safestep/3_yellowborder.jpg", label: "Yellow Border colorway", fit: "contain" },
      { src: "/products/safestep/2_solidblack.jpg", label: "Solid Black colorway", fit: "contain" },
      { src: "/products/safestep/6_diagram.jpg", label: "Fusion-bonded composite construction", fit: "contain" },
    ],
    swatches: [
      { name: "Solid Black", src: "/products/safestep/swatches/solid-black.jpg" },
      { name: "Yellow Border", src: "/products/safestep/swatches/yellow-border.jpg" },
    ],
    description: [
      "Diamond Plate Safety Step brings purposeful material design to demanding workspaces where employees spend long hours on their feet. Its resilient foam foundation provides supportive cushioning that helps ease pressure on legs and joints, encouraging greater comfort throughout the workday.",
      "A durable vinyl-nitrile wear layer is polymerically bonded without adhesives, creating a strong, cohesive surface engineered for lasting performance. Finished with the distinctive character of industrial diamond plate and designed for easy cleaning, Diamond Plate Safety Step balances rugged strength, underfoot comfort, and dependable workplace protection.",
    ],
    applications: [
      "Fusion-bonded Composite",
      "3/16” Solid PVC Diamond-patterned",
      "9/16” Diamond-embossed anti-fatigue mats",
    ],
    features: [
      "Dependable anti-fatigue comfort with long-lasting performance",
      "Closed-cell PVC Foam cushioning",
    ],
  },
  "industrial-anti-fatigue-mats/bubble-mat": {
    productPdf: "/docs/anti-fatigue/bubble-mat_product-sheet.pdf",
    techSpecs: "/docs/anti-fatigue/bubble-mat_technical-specs.pdf",
    image: "/products/bubble-mat/1_checkout.jpg",
    gallery: [
      { src: "/products/bubble-mat/1_checkout.jpg", label: "Self-checkout station" },
      { src: "/products/bubble-mat/2_workstation.jpg", label: "Workstation comfort mat" },
      { src: "/products/bubble-mat/3_detail.jpg", label: "Molded-bubble surface detail" },
    ],
    swatches: [
      { name: "Black Molded Rubber", src: "/products/bubble-mat/swatches/black.jpg" },
    ],
    description: [
      "Bubble Mat brings purposeful form and supportive comfort to workspaces where employees spend long hours standing. Its distinctive molded-bubble surface is crafted from resilient, 12.7 mm-thick rubber, creating a cushioned foundation that helps relieve pressure on feet, legs, and joints.",
      "Designed to encourage comfort throughout the workday, Bubble Mat helps reduce fatigue and the physical strain associated with hard flooring. The result is a thoughtfully engineered anti-fatigue solution that supports employee well-being, safer movement, and sustained productivity.",
    ],
    applications: [
      "Beveled edges increase safety by reducing potential tripping & slipping",
      "Air pockets provide timeless comfort",
      "Grease/Oil Resistant",
    ],
    features: [
      'Thickness: 1/2" Molded Rubber Anti-Fatigue matting',
      "Material: 100% Black Molded Rubber",
    ],
  },
  "industrial-anti-fatigue-mats/elite-step": {
    productPdf: "/docs/anti-fatigue/elite-step_product-sheet.pdf",
    techSpecs: "/docs/anti-fatigue/elite-step_technical-specs.pdf",
    image: "/products/elite-step/1_office.jpg",
    gallery: [
      { src: "/products/elite-step/1_office.jpg", label: "Office workstation" },
      { src: "/products/elite-step/2_kitchen.jpg", label: "Kitchen prep station" },
    ],
    swatches: [
      { name: "Black", src: "/products/elite-step/swatches/black.jpg" },
      { name: "Brown", src: "/products/elite-step/swatches/brown.jpg" },
    ],
    description: [
      "Elite Step brings tailored character and supportive comfort to workspaces where employees spend long hours standing. Its cushioned anti-fatigue foundation helps ease stress on legs and joints, while the refined faux-leather finish creates a polished surface suited to more design-conscious environments.",
      "A durable vinyl wear layer resists moisture and punctures while remaining easy to clean, and beveled edges create a smooth transition that helps reduce trip hazards. The result is a thoughtfully crafted matting solution that balances premium appearance, lasting protection, and everyday comfort.",
    ],
    applications: [
      "Solid vinyl wear layer polymerically bound to soft, moisture-resistant closed-cell vinyl",
      "Matte leather-grain emboss surface aspect",
      "1/2\u201d (470mil) Overall Gauge",
    ],
    features: [
      "Available Sizes: 2\u2019 x 3\u2019 | 3\u2019 x 5\u2019 | 3\u2019 x 10\u2019 | 3\u2019 x 12\u2019 | 3\u2019 x 60\u2019",
      "Elite Step can be mopped or wiped clean with a damp cloth.",
    ],
  },
  "industrial-anti-fatigue-mats/luxury-step": {
    productPdf: "/docs/anti-fatigue/luxury-step_product-sheet.pdf",
    techSpecs: "/docs/anti-fatigue/luxury-step_technical-specs.pdf",
    image: "/products/luxury-step/2_retail.jpg",
    gallery: [
      { src: "/products/luxury-step/2_retail.jpg", label: "Office workstation" },
      { src: "/products/luxury-step/1_office.jpg", label: "Retail checkout station" },
      { src: "/products/luxury-step/3_product.jpg", label: "Matte surface detail" },
    ],
    swatches: [
      { name: "Black", src: "/products/luxury-step/swatches/black.jpg" },
    ],
    description: [
      "Luxury Step brings understated refinement and supportive comfort to workspaces where employees spend extended periods standing. Its expanded foam sponge construction creates a resilient, cushioned foundation that helps reduce pressure on legs and joints throughout the workday.",
      "Finished with a smooth matte wear layer, Luxury Step offers a clean, contemporary appearance while resisting punctures and simplifying routine care. The result is a thoughtfully engineered anti-fatigue surface that balances practical durability, everyday comfort, and polished design.",
    ],
    applications: [
      "Solid vinyl wear layer polymerically bound to soft foam",
      "Smooth, non-embossed matte finish",
      "Custom Sizes available at a Premium",
    ],
    features: [
      'Thickness: 1/2" Anti-Fatigue matting',
      "Material: Moisture-resistant closed-cell vinyl sponge",
    ],
  },
  "industrial-anti-fatigue-mats/marble-step": {
    productPdf: "/docs/anti-fatigue/marble-step_product-sheet.pdf",
    techSpecs: "/docs/anti-fatigue/marble-step_technical-specs.pdf",
    image: "/products/marble-step/1_retail.jpg",
    gallery: [
      { src: "/products/marble-step/1_retail.jpg", label: "Retail check stand" },
      { src: "/products/marble-step/2_spa.jpg", label: "Spa and wellness reception" },
    ],
    swatches: [
      { name: "Grey/White", src: "/products/marble-step/swatches/grey-white.jpg" },
      { name: "Black/White", src: "/products/marble-step/swatches/black-white.jpg" },
      { name: "Blue/White", src: "/products/marble-step/swatches/blue-white.jpg" },
    ],
    description: [
      "Marble Step brings classic beauty and dependable support to workspaces where employees spend long hours standing. Its smooth, marbleized wear layer is puncture-resistant and easy to clean, while the expanded foam sponge underside reduces stress on legs and joints for added comfort and performance throughout the day.",
      "Beveled edges help reduce trip, slip, and fall accidents, and its moisture-resistant engineering makes it suitable for most any environment. The result is a thoughtfully designed anti-fatigue mat that pairs refined appearance with lasting durability and everyday comfort.",
    ],
    applications: [
      "Suggested uses: check stands, floral departments, offices, and light-duty work areas",
      "Beveled edges reduce trip, slip, and fall accidents",
      "Moisture-resistant engineering suitable for most any environment",
    ],
    features: [
      'Gauge: ~1/2" (0.50") \u2014 ~1/8" solid marbleized PVC wear surface over 3/8" Ergo Flex closed-cell foam',
      "Available Colors: Grey (standard), Blue, Tan, Black, Brown",
      "Available Sizes: 2\u2019 x 3\u2019 | 2\u2019 x 60\u2019 | 3\u2019 x 5\u2019 | 3\u2019 x 60\u2019 | 4\u2019 x 60\u2019",
      "Marble Step can be mopped or wiped clean with a damp cloth.",
    ],
  },
};

let counter = 0;
export const categories = RAW.map((cat) => ({
  slug: cat.slug,
  name: cat.name,
  image: cat.image,
  blurb: cat.blurb,
  products: cat.products.map((pname) => {
    counter += 1;
    const slug = slugify(pname);
    const override = OVERRIDES[`${cat.slug}/${slug}`] || {};
    return {
      id: counter,
      slug,
      name: pname,
      categorySlug: cat.slug,
      categoryName: cat.name,
      image: override.image || cat.image,
      isPlaceholder: !override.image,
      description:
        override.description ||
        `${pname} is part of the van Gelder ${cat.name} line. ${cat.blurb} This is placeholder content for the temporary site and can be edited later with final product copy.`,
      applications: override.applications || applicationsFor(cat.name),
      features: override.features || featuresFor(cat.name),
      gallery: override.gallery || null,
      swatches: override.swatches || null,
      swatchNote: override.swatchNote || null,
      certifications: override.certifications || null,
      swatchCount: override.swatchCount || 38,
      hideSwatches: override.hideSwatches || false,
      thumbFit: override.thumbFit || null,
      productPdf: override.productPdf || null,
      techSpecs: override.techSpecs || null,
    };
  }),
}));

export const allProducts = categories.flatMap((c) => c.products);

export const totalProductCount = allProducts.length;

export const getCategory = (slug) => categories.find((c) => c.slug === slug);

export const getProduct = (categorySlug, productSlug) => {
  const cat = getCategory(categorySlug);
  if (!cat) return null;
  return cat.products.find((p) => p.slug === productSlug) || null;
};

export const CONTACT_INFO = {
  company: "van Gelder, Inc.",
  address: "300 Union Grove Rd SE, Calhoun, Georgia 30701",
  phone: "+1 706 602 4186",
  phoneHref: "tel:+17066024186",
};
